import React, { useState, useEffect, useMemo } from 'react';
import { db, getCollectionName, auth } from './firebase';
import ConciliacaoRemocoes from './remocoes/ConciliacaoRemocoes';
import AphSOS from './remocoes/AphSOS';
import {
  collection, query, onSnapshot, orderBy, doc,
  addDoc, updateDoc, deleteDoc, setDoc, serverTimestamp,
  getDocs, where, campos,
} from './firestore-compat';

// ── Constantes ────────────────────────────────────────────────────────────────
const OPERADORAS = ['AMIL','BRADESCO','PEND. AMIL','UNIMED baby','Unimed senior','Klini','Unimed Brasil','Unimed Nova Iguaçu','Unimed Leste','Unimed Outros'];
const EMPRESAS_REMOCAO_FALLBACK = ['Operadora','SOS Vida','Fly Remoções','Removip'];
const SOLICITANTES = ['Núcleo Captação','Núcleo Intercorrência','Familiar','Operadora','Outro'];
const TIPOS = ['ELETIVO','URG. PROGR.','INTERCORRÊNCIA','DESOSPITALIZAÇÃO'];
const PERIODICIDADE_OPTS = ['Mensal','2 meses','3 meses','4 meses','5 meses','6 meses'];
const MES_OPTS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const UNIMED_OUTROS_OPTS = ['Unimed Leste','Unimed Nova Iguaçu','Unimed Outros','Acompanhar com a mãe'];

const STATUS_AGENDADAS = [
  'Prévia','Em análise','Inserir no portal','Aguardando pedido','Pendente',
  'Aut. retroativa','Autorizado','Pacote','Realizado',
  'Negado','Cancelado','Hospitalizado(a)',
];

// Progresso interno da tarefa — separado do STATUS (que é sobre autorização/operadora)
const ANDAMENTO_OPTS = ['Não iniciado','Em andamento','Concluído'];
const ANDAMENTO_COLOR = {
  'Não iniciado': '#64748b',
  'Em andamento': '#a16207',
  'Concluído':    '#15803d',
};

const STATUS_COLOR = {
  'Prévia':            { bg:'#ede9fe', text:'#7c3aed' },
  'Autorizado':        { bg:'#dcfce7', text:'#15803d' },
  'Realizado':         { bg:'#d1fae5', text:'#065f46' },
  'Em análise':        { bg:'#fef9c3', text:'#a16207' },
  'Aguardando pedido': { bg:'#fef9c3', text:'#a16207' },
  'Pendente':          { bg:'#fef9c3', text:'#a16207' },
  'Inserir no portal': { bg:'#fef9c3', text:'#a16207' },
  'Negado':            { bg:'#fee2e2', text:'#b91c1c' },
  'Cancelado':         { bg:'#fee2e2', text:'#b91c1c' },
  'Aut. retroativa':   { bg:'#dbeafe', text:'#1d4ed8' },
  'Pacote':            { bg:'#dbeafe', text:'#1d4ed8' },
  'Hospitalizado(a)':  { bg:'#f1f5f9', text:'#64748b' },
};

// Paleta de cores disponível para novos status criados pelo admin
const STATUS_PALETA = [
  { corBg:'#ede9fe', corText:'#7c3aed' }, // roxo
  { corBg:'#fef9c3', corText:'#a16207' }, // amarelo
  { corBg:'#dcfce7', corText:'#15803d' }, // verde
  { corBg:'#fee2e2', corText:'#b91c1c' }, // vermelho
  { corBg:'#dbeafe', corText:'#1d4ed8' }, // azul
  { corBg:'#d1fae5', corText:'#065f46' }, // teal
  { corBg:'#fce7f3', corText:'#9d174d' }, // rosa
  { corBg:'#f1f5f9', corText:'#64748b' }, // cinza
];

// Pipeline Kanban
const PIPELINE_COLUNAS = [
  { key:'previa',     label:'Prévia',         color:'#7c3aed', statuses:['Prévia']                                    },
  { key:'solicitado', label:'Solicitado',      color:'#d97706', statuses:['Em análise']                                },
  { key:'cotacao',    label:'Em Cotação',      color:'#2563eb', statuses:['Inserir no portal','Aguardando pedido']     },
  { key:'aguardando', label:'Ag. Aprovação',   color:'#ea580c', statuses:['Pendente','Aut. retroativa']               },
  { key:'agendado',   label:'Agendado',        color:'#16a34a', statuses:['Autorizado','Pacote']                       },
  { key:'realizado',  label:'Realizado',       color:'#0d9488', statuses:['Realizado']                                 },
];
const PIPELINE_TERMINAL = ['Negado','Cancelado','Hospitalizado(a)'];

const OP_COLOR = {
  'AMIL':          { bg:'#ede9fe', text:'#6d28d9' },
  'BRADESCO':      { bg:'#fee2e2', text:'#b91c1c' },
  'PEND. AMIL':    { bg:'#fde68a', text:'#b45309' },
  'UNIMED baby':   { bg:'#d1fae5', text:'#065f46' },
  'Unimed senior': { bg:'#7c3aed', text:'#fff'    },
  'Klini':         { bg:'#fce7f3', text:'#9d174d' },
  'Unimed Brasil': { bg:'#d1fae5', text:'#065f46' },
};

const STATUS_CANULA = {
  'Em dia':   { bg:'#dcfce7', text:'#15803d' },
  'Próximo':  { bg:'#fef9c3', text:'#a16207' },
  'Atrasado': { bg:'#fee2e2', text:'#b91c1c' },
  'Sem data': { bg:'#f1f5f9', text:'#64748b' },
};

const P = {
  bg:'#f8fafc', white:'#fff', border:'#e2e8f0', text:'#1e293b',
  muted:'#64748b', primary:'#6366f1', green:'#22c55e',
  red:'#ef4444', amber:'#f59e0b', blue:'#3b82f6', gray:'#94a3b8',
};

const TABS = [
  { k:'pipeline',     l:'📊 Acompanhamento',       col:'rm_agendadas'    },
  { k:'conciliacao',  l:'🔎 Conciliação (SOS × IW × Quality)', col:'rm_agendadas' },
  { k:'aph',          l:'🚑 APH',                  col:'rm_agendadas'    },
  { k:'agendadas',    l:'Remoções Agendadas',      col:'rm_agendadas'    },
  { k:'dashboard_valores', l:'💰 Dashboard de Valores', col:'rm_agendadas' },
  { k:'para_agendar', l:'Para Agendar',            col:'rm_para_agendar' },
  { k:'implantacao_agendar', l:'🏥 Implantação',    col:'rm_para_agendar' },
  { k:'seriadas',     l:'Remoções Seriadas',       col:'rm_seriadas'     },
  // "Fazer Contato Médico" saiu (14/10, pedido do Alexandre); "Unimed Outros" e "Bradesco"
  // (cânulas) foram para a tela Dispositivos → "Cânulas e sondas em casa".
  { k:'cancelados',   l:'⚫ Cancelados',            col:'rm_agendadas'    },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function toSortDate(val) {
  if (!val || typeof val !== 'string') return '';
  const m = val.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}
function periodicidadeEmMeses(p) {
  return { 'Mensal':1,'2 meses':2,'3 meses':3,'4 meses':4,'5 meses':5,'6 meses':6 }[p] || 1;
}
function calcProxima(ultima, periodo) {
  if (!ultima || !periodo) return '';
  const d = new Date(ultima + 'T00:00');
  if (isNaN(d)) return '';
  d.setMonth(d.getMonth() + periodicidadeEmMeses(periodo));
  return d.toISOString().split('T')[0];
}
function calcStatusCanula(proxima) {
  if (!proxima) return 'Sem data';
  const diff = Math.ceil((new Date(proxima + 'T00:00') - new Date()) / 86400000);
  if (diff < 0) return 'Atrasado';
  if (diff <= 7) return 'Próximo';
  return 'Em dia';
}
function fmtDate(s) {
  if (!s || typeof s !== 'string') return '—';
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '—';
  return `${match[3]}/${match[2]}/${match[1]}`;
}
function todayISO() { return new Date().toISOString().split('T')[0]; }
function fmtMoeda(v) {
  const n = Number(v);
  if (!v || isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
}
function calcIdade(dn) {
  if (!dn) return null;
  const nasc = new Date(dn + 'T00:00');
  if (isNaN(nasc)) return null;
  const hoje = new Date();
  let idade = hoje.getFullYear() - nasc.getFullYear();
  const m = hoje.getMonth() - nasc.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) idade--;
  return idade;
}
// Remoção cujo paciente de origem foi cancelado pela operadora na Captação
function isCaptacaoCancelada(r, canceladosMap) {
  return !!(r && r.captacao_id && canceladosMap && canceladosMap[r.captacao_id]);
}

// ── Mini-componentes ──────────────────────────────────────────────────────────
function StatusBadge({ status, map = STATUS_COLOR, small }) {
  const c = map[status] || { bg:'#f1f5f9', text:'#64748b' };
  return (
    <span style={{ background:c.bg, color:c.text, padding:small?'2px 7px':'3px 10px',
      borderRadius:20, fontWeight:700, fontSize:small?'0.68rem':'0.74rem', whiteSpace:'nowrap' }}>
      {status || '—'}
    </span>
  );
}
function OpBadge({ op }) {
  const c = OP_COLOR[op] || { bg:'#e2e8f0', text:'#1e293b' };
  return (
    <span style={{ background:c.bg, color:c.text, padding:'2px 9px', borderRadius:20,
      fontWeight:700, fontSize:'0.72rem', whiteSpace:'nowrap' }}>{op || '—'}</span>
  );
}
function KpiCard({ label, value, color, onClick, sub }) {
  return (
    <div onClick={onClick} style={{ background:P.white, border:`1px solid ${P.border}`, borderRadius:10,
      padding:'12px 18px', minWidth:130, flex:1,
      cursor: onClick ? 'pointer' : 'default',
      transition:'box-shadow .15s',
      ...(onClick && { ':hover':{ boxShadow:'0 4px 12px rgba(0,0,0,.08)' } }),
    }}>
      <div style={{ fontSize:'0.68rem', fontWeight:700, color:P.muted, textTransform:'uppercase', letterSpacing:.5 }}>{label}</div>
      <div style={{ fontSize:'1.8rem', fontWeight:800, color, lineHeight:1.1, margin:'4px 0 0' }}>{value}</div>
      {sub && <div style={{ fontSize:'0.68rem', color:P.muted, marginTop:2 }}>{sub}</div>}
    </div>
  );
}
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
>
      <div style={{ background:P.white, borderRadius:14, width:'100%', maxWidth:wide?800:560,
        maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.25)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'16px 22px', borderBottom:`1px solid ${P.border}`,
          position:'sticky', top:0, background:P.white, zIndex:1 }}>
          <h2 style={{ margin:0, fontSize:'1rem', fontWeight:800, color:P.text }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'1.3rem', cursor:'pointer', color:P.muted }}>✕</button>
        </div>
        <div style={{ padding:'20px 22px' }}>{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:'0.71rem', fontWeight:700, color:P.muted,
        textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
        {label}{required && <span style={{ color:P.red }}> *</span>}
      </label>
      {children}
    </div>
  );
}
const inp = {
  width:'100%', padding:'8px 11px', border:`1px solid ${P.border}`, borderRadius:8,
  fontSize:'0.85rem', background:P.white, color:P.text, boxSizing:'border-box', outline:'none',
};
// Campo de paciente com busca na base já cadastrada (Cadastro de Pacientes + Captação/Implantação)
function PacienteInput({ value, onChange, onSelecionar, pacientesBase = [] }) {
  const [foco, setFoco] = useState(false);
  const sugestoes = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    if (q.length < 2) return [];
    return pacientesBase.filter(p => (p.nome || '').toLowerCase().includes(q)).slice(0, 8);
  }, [value, pacientesBase]);

  return (
    <div style={{ position:'relative' }}>
      <input
        value={value} onChange={e => onChange(e.target.value)}
        onFocus={() => setFoco(true)} onBlur={() => setTimeout(() => setFoco(false), 150)}
        placeholder="Nome completo do paciente" style={inp} autoComplete="off"
      />
      {foco && sugestoes.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200, background:P.white,
          border:`1px solid ${P.border}`, borderRadius:8, marginTop:2, boxShadow:'0 8px 24px rgba(0,0,0,.12)',
          maxHeight:220, overflowY:'auto' }}>
          {sugestoes.map(p => (
            <div key={`${p._origem}_${p.id}`} onClick={() => onSelecionar(p)}
              style={{ padding:'8px 12px', cursor:'pointer', borderBottom:'1px solid #f1f5f9' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ fontWeight:600, fontSize:'0.82rem', color:P.text }}>{p.nome}</div>
              <div style={{ fontSize:'0.7rem', color:P.muted }}>
                {p._origem === 'captacao' ? '🆕 Implantação' : '📋 Cadastro de Pacientes'}
                {p.dataNascimento ? ` · Nasc. ${fmtDate(p.dataNascimento)}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function Sel({ value, onChange, options, placeholder }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={inp}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function Btn({ children, onClick, disabled, color = P.primary, small }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ background:disabled?P.border:color, color:'#fff', border:'none',
        borderRadius:8, padding:small?'5px 12px':'8px 18px',
        fontWeight:700, fontSize:small?'0.78rem':'0.84rem',
        cursor:disabled?'default':'pointer', opacity:disabled?.6:1 }}>
      {children}
    </button>
  );
}
function GhostBtn({ children, onClick, small }) {
  return (
    <button onClick={onClick}
      style={{ background:'none', border:`1px solid ${P.border}`, color:P.muted,
        borderRadius:8, padding:small?'4px 10px':'7px 14px',
        fontWeight:600, fontSize:small?'0.78rem':'0.82rem', cursor:'pointer' }}>
      {children}
    </button>
  );
}

const TH = ({ children }) => (
  <th style={{ padding:'10px 12px', textAlign:'left', fontWeight:700, color:P.muted,
    fontSize:'0.71rem', textTransform:'uppercase', letterSpacing:.4,
    borderBottom:`1px solid ${P.border}`, whiteSpace:'nowrap', background:'#f8fafc' }}>
    {children}
  </th>
);

// ── Tab 1: Remoções Agendadas ─────────────────────────────────────────────────
const EMPTY_AG = {
  paciente:'', idade:'', dataNascimento:'', motivo:'', operadora:'', data:'', horario:'', solicitante:'',
  empresaRemocao:'', tipo:'ELETIVO', status:'Em análise', valorCotado:'', valorCobrado:'',
  local_origem:'', local_destino:'', cep_origem:'', cep_destino:'',
  tem_o2:false, tem_medico:false, tem_enfermeiro:false,
  protocolo:'', observacoes:'',
  num_iw:'', captacao_id:'', filial:'', origem_fluxo:'',
  aditivo_iw:'', input_impressos:'', andamento:'Não iniciado',
  horarioSaida:'', horarioEncerramento:'', km:'', acima50km:'',
  valorKm:'', horaParada:'', valorTotal:'', obsExtra:'',
};

const CANCELADOS_STATUS = ['Cancelado', 'Negado'];
// Feita (está na prévia da SOS) e sem aditivo no IW — o robô marca em r.sos.pendencia
const semAditivoSOS = r => !!r.sos?.pendencia && r.aditivo_iw !== 'Sim' && !CANCELADOS_STATUS.includes(r.status);
function TabAgendadas({ data, canceladosMap, statusOptions, statusColorMap, isAdmin, empresasRemocao = [], solicitantesRemocao = [], pacientesBase = [] }) {
  const empresasOpts = empresasRemocao.length > 0 ? empresasRemocao : EMPRESAS_REMOCAO_FALLBACK;
  const solicitantesOpts = solicitantesRemocao.length > 0 ? solicitantesRemocao : SOLICITANTES;
  const [alertaAberto, setAlertaAberto] = useState(false);
  const [verLocal, setVerLocal] = useState(null); // registro cujo Origem/Destino está sendo exibido
  const [subTab,   setSubTab]   = useState('agendadas');
  const [statusModal, setStatusModal] = useState(false);
  const [search,   setSearch]   = useState('');
  const [fStatus,  setFStatus]  = useState('');
  const [fOp,      setFOp]      = useState('');
  const [fTipo,    setFTipo]    = useState('');
  const [fEmpresa, setFEmpresa] = useState('');
  const [fDIni,    setFDIni]    = useState('');
  const [fDFim,    setFDFim]    = useState('');
  const [fSemAdt,  setFSemAdt]  = useState(false); // só as feitas (prévia da SOS) sem aditivo no IW
  const [modal,    setModal]    = useState(false);
  const [edit,     setEdit]     = useState(null);
  const [form,     setForm]     = useState(EMPTY_AG);
  const [saving,   setSaving]   = useState(false);
  const [delId,      setDelId]      = useState(null);
  const [inlineSt,   setInlineSt]   = useState(null);
  const [showHoje,   setShowHoje]   = useState(false);
  const [textoSolic, setTextoSolic] = useState('');
  const col = getCollectionName('rm_agendadas');

  const gerarTextoSolicitacao = () => {
    const dt     = form.data ? fmtDate(form.data) : '—';
    const hr     = form.horario || '—';
    const nome   = form.paciente || '—';
    const id     = form.idade ? ` (${form.idade} anos)` : '';
    const orig   = form.local_origem || '—';
    const cepO   = form.cep_origem ? ` — CEP: ${form.cep_origem}` : '';
    const dest   = form.local_destino || '—';
    const cepD   = form.cep_destino ? ` — CEP: ${form.cep_destino}` : '';
    const o2     = form.tem_o2 ? 'Sim' : 'Não';
    const med    = form.tem_medico ? 'Sim' : 'Não';
    const enf    = form.tem_enfermeiro ? 'Sim' : 'Não';
    const obs    = form.observacoes ? `\n\n📝 *Obs:* ${form.observacoes}` : '';
    const tipo   = form.tipo || '—';
    const op     = form.operadora || '—';
    const texto =
`*SOLICITAÇÃO DE REMOÇÃO*

*Paciente:* ${nome}${id}
*Data / Hora:* ${dt} às ${hr}
*Operadora:* ${op}

*Origem:* ${orig}${cepO}
*Destino:* ${dest}${cepD}

*Tipo de veículo:* ${tipo}
*O₂:* ${o2}
*Médico acompanhante:* ${med}
*Enfermeiro:* ${enf}${obs}`;
    setTextoSolic(texto);
    try { navigator.clipboard.writeText(texto); } catch {}
  };

  const openNew  = () => { setForm(EMPTY_AG); setEdit(null); setTextoSolic(''); setModal(true); };
  const openEdit = r  => { setForm({ ...EMPTY_AG, ...r }); setEdit(r); setTextoSolic(''); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); setTextoSolic(''); };

  const save = async () => {
    if (!form.paciente.trim()) return;
    setSaving(true);
    try {
      if (edit) await updateDoc(doc(db, col, edit.id), { ...form, updatedAt: serverTimestamp() });
      else      await addDoc(collection(db, col), { ...form, criadoEm: serverTimestamp() });
      close();
    } catch(e) { console.error(e); alert('Erro ao salvar: ' + (e.message || JSON.stringify(e))); }
    setSaving(false);
  };

  const quickStatus = async (r, status) => {
    await updateDoc(doc(db, col, r.id), { status, updatedAt: serverTimestamp() });
    setInlineSt(null);
  };

  const rows = useMemo(() => {
    let r = data;
    if (search)  r = r.filter(x => ((x.paciente||'')+(x.motivo||'')+(x.observacoes||'')).toLowerCase().includes(search.toLowerCase()));
    if (fStatus) r = r.filter(x => x.status    === fStatus);
    if (fOp)     r = r.filter(x => x.operadora === fOp);
    if (fTipo)   r = r.filter(x => x.tipo      === fTipo);
    if (fEmpresa) r = r.filter(x => x.empresaRemocao === fEmpresa);
    if (fDIni)   r = r.filter(x => x.data >= fDIni);
    if (fDFim)   r = r.filter(x => x.data <= fDFim);
    if (fSemAdt) r = r.filter(semAditivoSOS);
    return [...r].sort((a, b) => {
      const da = toSortDate(a.data);
      const db = toSortDate(b.data);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da < db ? -1 : da > db ? 1 : 0;
    });
  }, [data, search, fStatus, fOp, fTipo, fEmpresa, fDIni, fDFim, fSemAdt]);
  const qtdSemAditivoSOS = useMemo(() => data.filter(semAditivoSOS).length, [data]);

  // Concluído = autorizado como "Realizado" pela operadora OU o andamento interno já foi
  // marcado como "Concluído" — os dois levam a remoção pra fora da lista de pendentes,
  // pra não ficar uma remoção já finalizada internamente ainda aparecendo como se faltasse fazer.
  // Canceladas/Negadas também saem da lista principal — ficam na própria aba de canceladas.
  const rowsAgendadas  = useMemo(() => rows.filter(r => r.status !== 'Realizado' && r.andamento !== 'Concluído' && !CANCELADOS_STATUS.includes(r.status)), [rows]);
  const rowsConcluidas = useMemo(() => rows.filter(r => r.status === 'Realizado' || r.andamento === 'Concluído').sort((a, b) => {
    const da = toSortDate(a.data); const db = toSortDate(b.data);
    if (!da && !db) return 0; if (!da) return 1; if (!db) return -1;
    return da > db ? -1 : da < db ? 1 : 0;
  }), [rows]);
  const rowsCanceladas = useMemo(() => rows.filter(r => CANCELADOS_STATUS.includes(r.status)).sort((a, b) => {
    const da = toSortDate(a.data); const db = toSortDate(b.data);
    if (!da && !db) return 0; if (!da) return 1; if (!db) return -1;
    return da > db ? -1 : da < db ? 1 : 0;
  }), [rows]);
  const displayRows = subTab === 'concluidas' ? rowsConcluidas : subTab === 'canceladas' ? rowsCanceladas : rowsAgendadas;

  // ── Alertas de data/pendências ──────────────────────────────────────────────
  const jaPassou = r => {
    if (!r.data) return false;
    const dt = new Date(`${r.data}T${r.horario || '23:59'}`);
    return !isNaN(dt) && dt.getTime() < Date.now();
  };
  const faltaPapelada = r => !r.aditivo_iw || !r.input_impressos;

  // Próximas 72h, ainda não "Realizado" (diferente de "andamento concluído" — aqui é
  // sobre a autorização da operadora, o alerta é justamente pra quem ainda não autorizou)
  const alertas72h = useMemo(() => {
    const agora = Date.now();
    const limite = agora + 72 * 3600000;
    return rows.filter(r => {
      if (!r.data || r.status === 'Realizado') return false;
      const dt = new Date(`${r.data}T${r.horario || '00:00'}`);
      const t = dt.getTime();
      return !isNaN(t) && t >= agora && t <= limite;
    }).sort((a, b) => new Date(`${a.data}T${a.horario||'00:00'}`) - new Date(`${b.data}T${b.horario||'00:00'}`));
  }, [rows]);

  useEffect(() => {
    if (alertas72h.length > 0) setAlertaAberto(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertas72h.length > 0]);

  const hoje = todayISO();
  const kpis = useMemo(() => ({
    total:     rowsAgendadas.length,
    autorizado:rowsAgendadas.filter(x => x.status === 'Autorizado').length,
    pendente:  rowsAgendadas.filter(x => ['Em análise','Pendente','Aguardando pedido','Inserir no portal'].includes(x.status)).length,
    cancelado: rowsAgendadas.filter(x => ['Cancelado','Negado'].includes(x.status)).length,
    pacote:    rowsAgendadas.filter(x => x.status === 'Pacote').length,
    hoje:      data.filter(x => x.data === hoje && x.status !== 'Realizado'),
  }), [rowsAgendadas, data, hoje]);

  const hasF = search||fStatus||fOp||fTipo||fEmpresa||fDIni||fDFim||fSemAdt;
  const clrF = () => { setSearch(''); setFStatus(''); setFOp(''); setFTipo(''); setFEmpresa(''); setFDIni(''); setFDFim(''); setFSemAdt(false); };

  return (
    <div>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        <KpiCard label="Total"       value={kpis.total}           color={P.muted}    />
        <KpiCard label="Autorizadas" value={kpis.autorizado}      color={P.green}    />
        <KpiCard label="Pendentes"   value={kpis.pendente}        color={P.amber}    />
        <KpiCard label="Canceladas"  value={kpis.cancelado}       color={P.red}      />
        <KpiCard label="Pacote"      value={kpis.pacote}          color={P.blue}     />
        <KpiCard label="🚑 Hoje"    value={kpis.hoje.length}     color='#0d9488'
          sub={kpis.hoje.length ? 'ver lista →' : 'nenhuma hoje'}
          onClick={kpis.hoje.length ? ()=>setShowHoje(true) : undefined} />
        {alertas72h.length > 0 && (
          <KpiCard label="⚠️ Próx. 72h sem autorização" value={alertas72h.length} color="#b91c1c"
            sub="ver lista →" onClick={()=>setAlertaAberto(true)} />
        )}
      </div>

      {/* Sub-tabs: Agendadas / Concluídas — Concluídas junta Realizado (autorização) e
          andamento=Concluído (trabalho interno já feito), pra não ter duas abas "concluído" diferentes */}
      <div style={{ display:'flex', gap:0, marginBottom:14, background:'#f1f5f9', borderRadius:10, padding:3, width:'fit-content' }}>
        {[
          { k:'agendadas',  l:'⚡ Agendadas',  count: rowsAgendadas.length,  color:'#6366f1' },
          { k:'concluidas', l:'✅ Concluídas', count: rowsConcluidas.length, color:'#0d9488' },
          { k:'canceladas', l:'⚫ Canceladas', count: rowsCanceladas.length, color:'#64748b' },
        ].map(({ k, l, count, color }) => (
          <button key={k} onClick={() => setSubTab(k)} style={{
            border: 'none', borderRadius: 8, padding: '6px 18px', cursor: 'pointer',
            fontSize: '0.82rem', fontWeight: 700, transition: 'all 0.15s',
            background: subTab === k ? P.white : 'transparent',
            color: subTab === k ? color : '#94a3b8',
            boxShadow: subTab === k ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
          }}>
            {l}
            <span style={{ marginLeft: 6, background: subTab === k ? color : '#e2e8f0', color: subTab === k ? '#fff' : '#94a3b8',
              borderRadius: 20, padding: '1px 7px', fontSize: '0.7rem', fontWeight: 700 }}>
              {count}
            </span>
          </button>
        ))}
      </div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
        {subTab === 'agendadas' && <Btn onClick={openNew}>+ Nova Remoção</Btn>}
        {isAdmin && <GhostBtn onClick={()=>setStatusModal(true)}>⚙️ Gerenciar Status</GhostBtn>}
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar paciente ou motivo…"
          style={{ ...inp, maxWidth:260, flex:1 }} />
        <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{ ...inp, maxWidth:170 }}>
          <option value="">Todos os status</option>
          {statusOptions.filter(s => subTab === 'concluidas' ? s === 'Realizado' : subTab === 'canceladas' ? CANCELADOS_STATUS.includes(s) : (s !== 'Realizado' && !CANCELADOS_STATUS.includes(s))).map(s=><option key={s}>{s}</option>)}
        </select>
        <select value={fOp} onChange={e=>setFOp(e.target.value)} style={{ ...inp, maxWidth:160 }}>
          <option value="">Todas operadoras</option>
          {OPERADORAS.map(o=><option key={o}>{o}</option>)}
        </select>
        <select value={fTipo} onChange={e=>setFTipo(e.target.value)} style={{ ...inp, maxWidth:170 }}>
          <option value="">Todos tipos</option>
          {TIPOS.map(t=><option key={t}>{t}</option>)}
        </select>
        <select value={fEmpresa} onChange={e=>setFEmpresa(e.target.value)} style={{ ...inp, maxWidth:160 }}>
          <option value="">Empresa da Remoção</option>
          {empresasOpts.map(p=><option key={p}>{p}</option>)}
        </select>
        <input type="date" value={fDIni} onChange={e=>setFDIni(e.target.value)} style={{ ...inp, maxWidth:148 }} />
        <input type="date" value={fDFim} onChange={e=>setFDFim(e.target.value)} style={{ ...inp, maxWidth:148 }} />
        {qtdSemAditivoSOS > 0 && (
          <button type="button" onClick={()=>{ setFSemAdt(v=>!v); if (!fSemAdt) setSubTab('concluidas'); }}
            title="Remoções que a SOS já cobrou (prévia) e que ainda não têm aditivo no IW"
            style={{ border:`1px solid ${fSemAdt ? '#b91c1c' : '#fecaca'}`, background: fSemAdt ? '#fee2e2' : '#fff7f7', color:'#b91c1c', borderRadius:8, padding:'7px 12px', fontSize:'0.8rem', fontWeight:700, cursor:'pointer' }}>
            ⚠ Falta aditivo no IW · {qtdSemAditivoSOS}
          </button>
        )}
        {hasF && <GhostBtn onClick={clrF}>✕ Limpar</GhostBtn>}
        <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:P.muted }}>{displayRows.length} registro{displayRows.length!==1?'s':''}</span>
      </div>

      <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, overflowX:'auto' }}>
        <table style={{ borderCollapse:'collapse', width:'max-content', minWidth:'100%', fontSize:'0.75rem', tableLayout:'fixed' }}>
          <colgroup>
            <col style={{ width:150 }} />{/* Paciente */}
            <col style={{ width:100 }} />{/* Motivo */}
            <col style={{ width:110 }} />{/* Operadora */}
            <col style={{ width:90  }} />{/* Data/Hora */}
            <col style={{ width:110 }} />{/* Solicitante */}
            <col style={{ width:110 }} />{/* Empresa da Remoção */}
            <col style={{ width:100 }} />{/* Valor Cotado */}
            <col style={{ width:110 }} />{/* Valor Cobrado */}
            <col style={{ width:100 }} />{/* Tipo */}
            <col style={{ width:120 }} />{/* Status */}
            <col style={{ width:120 }} />{/* Status Andamento */}
            <col style={{ width:100 }} />{/* Aditivo IW */}
            <col style={{ width:100 }} />{/* Input Impressos */}
            <col style={{ width:56  }} />{/* Local */}
            <col style={{ width:70  }} />{/* Ações */}
            <col style={{ width:100 }} />{/* Horário de Saída */}
            <col style={{ width:110 }} />{/* Horário de Encerramento */}
            <col style={{ width:80  }} />{/* KM */}
            <col style={{ width:100 }} />{/* Acima 50 Km */}
            <col style={{ width:100 }} />{/* Valor KM */}
            <col style={{ width:100 }} />{/* Hora Parada */}
            <col style={{ width:110 }} />{/* Valor Total */}
            <col style={{ width:160 }} />{/* OBS */}
          </colgroup>
          <thead><tr>
            <TH>Paciente</TH><TH>Motivo</TH><TH>Operadora</TH><TH>Data / Hora</TH>
            <TH>Solicitante</TH><TH>Empresa da Remoção</TH><TH>Valor Cotado</TH><TH>Valor Cobrado à Operadora</TH><TH>Tipo</TH><TH>Status</TH><TH>Andamento</TH>
            <TH>Aditivo no IW</TH><TH>Input em Impressos</TH>
            <TH>Local</TH><TH>Ações</TH>
            <TH>Horário de Saída</TH><TH>Horário de Encerramento</TH><TH>KM</TH><TH>Acima 50 Km</TH>
            <TH>Valor KM</TH><TH>Hora Parada</TH><TH>Valor Total</TH><TH>OBS</TH>
          </tr></thead>
          <tbody>
            {displayRows.length === 0 && <tr><td colSpan={23} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum registro.</td></tr>}
            {displayRows.map((r,i) => {
              const cancelada = isCaptacaoCancelada(r, canceladosMap);
              const atrasoGrave = !cancelada && jaPassou(r) && faltaPapelada(r);
              const atrasoAndamento = !cancelada && !atrasoGrave && jaPassou(r) && r.andamento !== 'Concluído';
              const rowBg = cancelada ? '#111827' : atrasoGrave ? '#fca5a5' : atrasoAndamento ? '#fde047' : (i%2===0?'transparent':'#fafafa');
              return (
              <tr key={r.id} className={cancelada ? 'rm-cancelada' : undefined}
                title={atrasoGrave ? 'Data já passou e falta Aditivo no IW e/ou Input em Impressos' : atrasoAndamento ? 'Data já passou e o andamento ainda não foi marcado como Concluído' : undefined}
                style={{ borderBottom:`1px solid #f1f5f9`, background:rowBg }}>
                {/* Paciente */}
                <td style={{ padding:'6px 8px', fontWeight:600, color:P.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.paciente}>
                  {r.paciente}
                  {r.origem_fluxo==='captacao_previa' && (
                    <span style={{ marginLeft:5, background:'#ede9fe', color:'#7c3aed', fontSize:'0.62rem', fontWeight:700, padding:'1px 5px', borderRadius:10, verticalAlign:'middle' }} title={`Nº IW: ${r.num_iw||'—'}`}>Cap.</span>
                  )}
                  {r.origem==='IW' && (
                    <span style={{ marginLeft:5, background:'#ecfdf5', color:'#065f46', fontSize:'0.62rem', fontWeight:700, padding:'1px 5px', borderRadius:10, verticalAlign:'middle' }}
                      title={`Pedido no IW em ${fmtDate(r.iw_data_pedido) || '—'}${r.iw_profissional ? ` por ${r.iw_profissional}` : ''}`}>🗂 IW</span>
                  )}
                  {r.sos && (
                    <span style={{ marginLeft:5, background:'#e0f2fe', color:'#075985', fontSize:'0.62rem', fontWeight:700, padding:'1px 5px', borderRadius:10, verticalAlign:'middle' }}
                      title={`Na prévia da SOS: ${fmtDate(r.sos.data)} · ${fmtMoeda(r.sos.valor)}${r.sos.linhas > 1 ? ` (${r.sos.linhas} trechos)` : ''} · ${r.sos.motivo || ''}${r.concluidoPor ? `
Concluída sozinha pelo robô em ${fmtDate(String(r.concluidoEm || '').slice(0, 10))}` : ''}`}>🧾 SOS</span>
                  )}
                </td>
                {/* Motivo */}
                <td style={{ padding:'6px 8px', color:P.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.motivo}>{r.motivo||'—'}</td>
                {/* Operadora */}
                <td style={{ padding:'6px 8px' }}><OpBadge op={r.operadora} /></td>
                {/* Data / Hora */}
                <td style={{ padding:'6px 8px', color:P.text, whiteSpace:'nowrap' }}>
                  <div>{fmtDate(r.data)||'—'}</div>
                  <div style={{ color:P.muted, fontSize:'0.7rem' }}>{r.horario||'—'}</div>
                </td>
                {/* Solicitante */}
                <td style={{ padding:'6px 8px', color:P.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.solicitante}>{r.solicitante||'—'}</td>
                {/* Empresa da Remoção */}
                <td style={{ padding:'6px 8px', color:P.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.empresaRemocao}>{r.empresaRemocao||'—'}</td>
                {/* Valor Cotado */}
                <td style={{ padding:'4px 6px' }}>
                  <input
                    type="number" step="0.01" defaultValue={r.valorCotado || ''} placeholder="R$"
                    onBlur={async e => {
                      const v = e.target.value;
                      if (v === String(r.valorCotado ?? '')) return;
                      await updateDoc(doc(db, col, r.id), { valorCotado: v === '' ? '' : Number(v), updatedAt: serverTimestamp() });
                    }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6, padding:'3px 5px', color:P.text, background:P.white }}
                  />
                  {r.sos && r.sos.valor > 0 && Number(r.valorCotado) !== r.sos.valor && (
                    <div style={{ fontSize:'0.62rem', color:'#b45309', fontWeight:700, marginTop:2 }} title="Valor da prévia da SOS diferente do valor cotado">SOS: {fmtMoeda(r.sos.valor)}</div>
                  )}
                </td>
                {/* Valor Cobrado à Operadora */}
                <td style={{ padding:'4px 6px' }}>
                  <input
                    type="number" step="0.01" defaultValue={r.valorCobrado || ''} placeholder="R$"
                    onBlur={async e => {
                      const v = e.target.value;
                      if (v === String(r.valorCobrado ?? '')) return;
                      await updateDoc(doc(db, col, r.id), { valorCobrado: v === '' ? '' : Number(v), updatedAt: serverTimestamp() });
                    }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6, padding:'3px 5px', color:P.text, background:P.white }}
                  />
                </td>
                {/* Tipo */}
                <td style={{ padding:'6px 8px' }}>
                  <span style={{ background:'#eff6ff', color:'#1d4ed8', padding:'2px 6px', borderRadius:20, fontWeight:600, fontSize:'0.68rem', whiteSpace:'nowrap', overflow:'hidden', display:'block', textOverflow:'ellipsis' }} title={r.tipo}>{r.tipo||'—'}</span>
                </td>
                {/* Status */}
                <td style={{ padding:'6px 8px', position:'relative' }}>
                  <div onClick={()=>setInlineSt(inlineSt===r.id?null:r.id)} style={{ cursor:'pointer' }}>
                    <StatusBadge status={r.status} map={statusColorMap} />
                  </div>
                  {inlineSt === r.id && (
                    <div style={{ position:'absolute', top:'100%', left:0, zIndex:100, background:P.white,
                      border:`1px solid ${P.border}`, borderRadius:10, padding:8,
                      boxShadow:'0 8px 24px rgba(0,0,0,.12)', minWidth:200 }}>
                      {statusOptions.map(s => (
                        <div key={s} onClick={()=>quickStatus(r,s)}
                          style={{ padding:'6px 10px', cursor:'pointer', borderRadius:7 }}
                          onMouseEnter={e=>e.currentTarget.style.background='#f1f5f9'}
                          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <StatusBadge status={s} small map={statusColorMap} />
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                {/* Status Andamento */}
                <td style={{ padding:'4px 6px' }}>
                  <select
                    value={r.andamento || 'Não iniciado'}
                    onChange={async e => { await updateDoc(doc(db, col, r.id), { andamento: e.target.value, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6,
                      padding:'3px 4px', color: ANDAMENTO_COLOR[r.andamento || 'Não iniciado'],
                      fontWeight:700, background:P.white, cursor:'pointer' }}>
                    {ANDAMENTO_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </td>
                {/* Aditivo no IW */}
                <td style={{ padding:'4px 6px' }}>
                  <select
                    value={r.aditivo_iw || ''}
                    onChange={async e => { await updateDoc(doc(db, col, r.id), { aditivo_iw: e.target.value, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6,
                      padding:'3px 4px', color: r.aditivo_iw==='Sim'?'#15803d':r.aditivo_iw==='Não'?'#b91c1c':P.muted,
                      fontWeight: r.aditivo_iw?700:400, background:P.white, cursor:'pointer' }}>
                    <option value="">—</option>
                    <option value="Sim">Sim</option>
                    <option value="Não">Não</option>
                    <option value="N/A">N/A</option>
                  </select>
                  {r.sos?.pendencia && r.aditivo_iw !== 'Sim' && (
                    <div style={{ fontSize:'0.62rem', fontWeight:700, marginTop:2, color: r.sos.pendencia === 'falta aditivo no IW' ? '#b91c1c' : '#c2410c' }}
                      title={r.sos.pendencia === 'falta aditivo no IW' ? 'Remoção fora do pacote (eletiva/transferência) feita pela SOS e sem aditivo no IW: lançar o aditivo para cobrar da operadora' : 'Remoção dentro do pacote feita pela SOS e sem aditivo no IW: lançar o aditivo para registrar o custo'}>
                      ⚠ {r.sos.pendencia === 'falta aditivo no IW' ? 'falta aditivo' : 'aditivo (pacote)'}
                    </div>
                  )}
                  {r.sos?.aditivoIW && <div style={{ fontSize:'0.62rem', color:'#15803d', marginTop:2 }} title={r.sos.aditivoIW.item}>IW {fmtDate(String(r.sos.aditivoIW.data || '').slice(0, 10))}</div>}
                </td>
                {/* Input em Impressos */}
                <td style={{ padding:'4px 6px' }}>
                  <select
                    value={r.input_impressos || ''}
                    onChange={async e => { await updateDoc(doc(db, col, r.id), { input_impressos: e.target.value, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6,
                      padding:'3px 4px', color: r.input_impressos==='Sim'?'#15803d':r.input_impressos==='Não'?'#b91c1c':P.muted,
                      fontWeight: r.input_impressos?700:400, background:P.white, cursor:'pointer' }}>
                    <option value="">—</option>
                    <option value="Sim">Sim</option>
                    <option value="Não">Não</option>
                    <option value="N/A">N/A</option>
                  </select>
                </td>
                {/* Local (Origem/Destino ocultos — abre popup) */}
                <td style={{ padding:'6px 8px', textAlign:'center' }}>
                  <button onClick={()=>setVerLocal(r)} title="Ver origem e destino"
                    style={{ border:`1px solid ${P.border}`, background:P.white, borderRadius:6, padding:'3px 7px', cursor:'pointer', fontSize:'0.82rem' }}>
                    📍
                  </button>
                </td>
                {/* Ações */}
                <td style={{ padding:'6px 8px', whiteSpace:'nowrap' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
                    <GhostBtn small onClick={()=>setDelId(r.id)}>🗑</GhostBtn>
                  </div>
                </td>
                {/* Horário de Saída */}
                <td style={{ padding:'4px 6px' }}>
                  <input type="time" defaultValue={r.horarioSaida || ''}
                    onBlur={async e => { const v = e.target.value; if (v === (r.horarioSaida||'')) return; await updateDoc(doc(db, col, r.id), { horarioSaida: v, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6, padding:'3px 5px', color:P.text, background:P.white }} />
                </td>
                {/* Horário de Encerramento */}
                <td style={{ padding:'4px 6px' }}>
                  <input type="time" defaultValue={r.horarioEncerramento || ''}
                    onBlur={async e => { const v = e.target.value; if (v === (r.horarioEncerramento||'')) return; await updateDoc(doc(db, col, r.id), { horarioEncerramento: v, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6, padding:'3px 5px', color:P.text, background:P.white }} />
                </td>
                {/* KM */}
                <td style={{ padding:'4px 6px' }}>
                  <input type="number" step="0.1" defaultValue={r.km ?? ''} placeholder="Km"
                    onBlur={async e => { const v = e.target.value; if (v === String(r.km ?? '')) return; await updateDoc(doc(db, col, r.id), { km: v === '' ? '' : Number(v), updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6, padding:'3px 5px', color:P.text, background:P.white }} />
                </td>
                {/* Acima 50 Km */}
                <td style={{ padding:'4px 6px' }}>
                  <input type="number" step="0.1" defaultValue={r.acima50km ?? ''} placeholder="Km"
                    onBlur={async e => { const v = e.target.value; if (v === String(r.acima50km ?? '')) return; await updateDoc(doc(db, col, r.id), { acima50km: v === '' ? '' : Number(v), updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6, padding:'3px 5px', color:P.text, background:P.white }} />
                </td>
                {/* Valor KM */}
                <td style={{ padding:'4px 6px' }}>
                  <input type="number" step="0.01" defaultValue={r.valorKm ?? ''} placeholder="R$"
                    onBlur={async e => { const v = e.target.value; if (v === String(r.valorKm ?? '')) return; await updateDoc(doc(db, col, r.id), { valorKm: v === '' ? '' : Number(v), updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6, padding:'3px 5px', color:P.text, background:P.white }} />
                </td>
                {/* Hora Parada */}
                <td style={{ padding:'4px 6px' }}>
                  <input type="text" defaultValue={r.horaParada || ''} placeholder="Ex.: 1h30"
                    onBlur={async e => { const v = e.target.value; if (v === (r.horaParada||'')) return; await updateDoc(doc(db, col, r.id), { horaParada: v, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6, padding:'3px 5px', color:P.text, background:P.white }} />
                </td>
                {/* Valor Total */}
                <td style={{ padding:'4px 6px' }}>
                  <input type="number" step="0.01" defaultValue={r.valorTotal ?? ''} placeholder="R$"
                    onBlur={async e => { const v = e.target.value; if (v === String(r.valorTotal ?? '')) return; await updateDoc(doc(db, col, r.id), { valorTotal: v === '' ? '' : Number(v), updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6, padding:'3px 5px', color:P.text, background:P.white, fontWeight:700 }} />
                </td>
                {/* OBS */}
                <td style={{ padding:'4px 6px' }}>
                  <input type="text" defaultValue={r.obsExtra || ''} placeholder="Observação"
                    onBlur={async e => { const v = e.target.value; if (v === (r.obsExtra||'')) return; await updateDoc(doc(db, col, r.id), { obsExtra: v, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6, padding:'3px 5px', color:P.text, background:P.white }} />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showHoje && (
        <Modal title={`🚑 Remoções de Hoje — ${new Date().toLocaleDateString('pt-BR')}`} onClose={()=>setShowHoje(false)} wide>
          {kpis.hoje.length === 0 ? (
            <p style={{ color:P.muted, textAlign:'center', padding:'20px 0' }}>Nenhuma remoção agendada para hoje.</p>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
              <thead>
                <tr style={{ borderBottom:`2px solid ${P.border}` }}>
                  {['Horário','Paciente','Operadora','Tipo','Status','Origem → Destino'].map(h => (
                    <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:P.muted, fontWeight:700, fontSize:'0.69rem', textTransform:'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...kpis.hoje].sort((a,b)=>(a.horario||'').localeCompare(b.horario||'')).map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid #f1f5f9`, background: i%2===0?'transparent':'#fafafa' }}>
                    <td style={{ padding:'7px 10px', fontWeight:700, color:'#0d9488' }}>{r.horario||'—'}</td>
                    <td style={{ padding:'7px 10px', fontWeight:600, color:P.text }}>{r.paciente}{r.idade?` (${r.idade}a)`:''}</td>
                    <td style={{ padding:'7px 10px' }}><OpBadge op={r.operadora} /></td>
                    <td style={{ padding:'7px 10px', color:P.text }}>{r.tipo||'—'}</td>
                    <td style={{ padding:'7px 10px' }}><StatusBadge status={r.status} small map={statusColorMap} /></td>
                    <td style={{ padding:'7px 10px', color:P.muted, fontSize:'0.74rem' }}>
                      <span>{r.local_origem||'—'}</span>
                      <span style={{ color:'#94a3b8', margin:'0 4px' }}>→</span>
                      <span>{r.local_destino||'—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}

      {verLocal && (
        <Modal title={`📍 Origem e Destino — ${verLocal.paciente}`} onClose={()=>setVerLocal(null)}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div>
              <div style={{ fontSize:'0.7rem', fontWeight:700, color:P.muted, textTransform:'uppercase', marginBottom:4 }}>Origem</div>
              <div style={{ color:P.text, fontWeight:600 }}>{verLocal.local_origem || '—'}</div>
              {verLocal.cep_origem && <div style={{ color:P.muted, fontSize:'0.78rem' }}>CEP: {verLocal.cep_origem}</div>}
            </div>
            <div>
              <div style={{ fontSize:'0.7rem', fontWeight:700, color:P.muted, textTransform:'uppercase', marginBottom:4 }}>Destino</div>
              <div style={{ color:P.text, fontWeight:600 }}>{verLocal.local_destino || '—'}</div>
              {verLocal.cep_destino && <div style={{ color:P.muted, fontSize:'0.78rem' }}>CEP: {verLocal.cep_destino}</div>}
            </div>
          </div>
        </Modal>
      )}

      {alertaAberto && alertas72h.length > 0 && (
        <Modal title="⚠️ Remoções nas próximas 72h sem autorização" onClose={()=>setAlertaAberto(false)} wide>
          <p style={{ color:P.muted, fontSize:'0.82rem', marginBottom:14 }}>
            Essas remoções estão marcadas para acontecer dentro das próximas 72 horas e ainda não têm o status "Realizado".
          </p>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
            <thead>
              <tr style={{ borderBottom:`2px solid ${P.border}` }}>
                {['Data / Hora','Paciente','Operadora','Status'].map(h => (
                  <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:P.muted, fontWeight:700, fontSize:'0.69rem', textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {alertas72h.map((r,i) => (
                <tr key={r.id} style={{ borderBottom:`1px solid #f1f5f9`, background: i%2===0?'transparent':'#fafafa' }}>
                  <td style={{ padding:'7px 10px', fontWeight:700, color:'#b91c1c' }}>{fmtDate(r.data)} {r.horario||''}</td>
                  <td style={{ padding:'7px 10px', fontWeight:600, color:P.text }}>{r.paciente}</td>
                  <td style={{ padding:'7px 10px' }}><OpBadge op={r.operadora} /></td>
                  <td style={{ padding:'7px 10px' }}><StatusBadge status={r.status} small map={statusColorMap} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}

      {modal && (
        <Modal title={edit?'Editar Remoção':'Nova Remoção'} onClose={close} wide>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 18px' }}>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Paciente" required>
                <PacienteInput
                  value={form.paciente} onChange={v=>setForm(f=>({...f,paciente:v}))} pacientesBase={pacientesBase}
                  onSelecionar={p => {
                    const idade = calcIdade(p.dataNascimento);
                    setForm(f => ({ ...f, paciente:p.nome, dataNascimento:p.dataNascimento||f.dataNascimento, idade: idade!=null?String(idade):f.idade }));
                  }}
                />
              </Field>
            </div>
            <Field label="Idade (anos)">
              <input type="number" min="0" max="130" value={form.idade||''} onChange={e=>setForm(f=>({...f,idade:e.target.value}))} placeholder="Ex.: 65" style={inp} />
            </Field>
            <Field label="Data de Nascimento">
              <input type="date" value={form.dataNascimento||''} onChange={e=>setForm(f=>({...f,dataNascimento:e.target.value}))} style={inp} />
            </Field>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Motivo" required>
                <input value={form.motivo} onChange={e=>setForm(f=>({...f,motivo:e.target.value}))} placeholder="Ex.: Troca de cânula, Consulta, Terapia, Exame, Desospitalização…" style={inp} />
              </Field>
            </div>
            <Field label="Operadora" required><Sel value={form.operadora} onChange={v=>setForm(f=>({...f,operadora:v}))} options={OPERADORAS} placeholder="Selecionar…" /></Field>
            <Field label="Data do atendimento" required><input type="date" value={form.data} onChange={e=>setForm(f=>({...f,data:e.target.value}))} style={inp} /></Field>
            <Field label="Horário" required><input type="time" value={form.horario} onChange={e=>setForm(f=>({...f,horario:e.target.value}))} style={inp} /></Field>
            <Field label="Solicitante"><Sel value={form.solicitante} onChange={v=>setForm(f=>({...f,solicitante:v}))} options={solicitantesOpts} placeholder="Selecionar…" /></Field>
            <Field label="Empresa da Remoção"><Sel value={form.empresaRemocao} onChange={v=>setForm(f=>({...f,empresaRemocao:v}))} options={empresasOpts} placeholder="Selecionar…" /></Field>
            <Field label="Tipo"><Sel value={form.tipo} onChange={v=>setForm(f=>({...f,tipo:v}))} options={TIPOS} /></Field>
            <Field label="Status"><Sel value={form.status} onChange={v=>setForm(f=>({...f,status:v}))} options={statusOptions} /></Field>
            <Field label="Valor Cotado">
              <input type="number" step="0.01" value={form.valorCotado} onChange={e=>setForm(f=>({...f,valorCotado:e.target.value}))} placeholder="0,00" style={inp} />
            </Field>
            <Field label="Valor Cobrado à Operadora">
              <input type="number" step="0.01" value={form.valorCobrado} onChange={e=>setForm(f=>({...f,valorCobrado:e.target.value}))} placeholder="0,00" style={inp} />
            </Field>
            <Field label="Local de origem">
              <input value={form.local_origem} onChange={e=>setForm(f=>({...f,local_origem:e.target.value}))} placeholder="Ex.: Domicílio, UPA, UTI Prontobaby…" style={inp} />
            </Field>
            <Field label="Local de destino">
              <input value={form.local_destino} onChange={e=>setForm(f=>({...f,local_destino:e.target.value}))} placeholder="Ex.: Hospital X, Clínica Y, Domicílio…" style={inp} />
            </Field>
            <Field label="CEP de origem">
              <input value={form.cep_origem||''} onChange={e=>setForm(f=>({...f,cep_origem:e.target.value}))} placeholder="00000-000" style={inp} maxLength={9} />
            </Field>
            <Field label="CEP de destino">
              <input value={form.cep_destino||''} onChange={e=>setForm(f=>({...f,cep_destino:e.target.value}))} placeholder="00000-000" style={inp} maxLength={9} />
            </Field>
            {/* Acompanhantes / equipamentos */}
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Recursos da remoção">
                <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                  {[
                    { key:'tem_o2',        label:'O₂'              },
                    { key:'tem_medico',    label:'Médico'          },
                    { key:'tem_enfermeiro',label:'Enfermeiro'      },
                  ].map(({ key, label }) => (
                    <label key={key} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer',
                      background: form[key] ? '#dcfce7' : '#f1f5f9',
                      border:`1px solid ${form[key] ? '#86efac' : P.border}`,
                      borderRadius:8, padding:'5px 12px', fontSize:'0.82rem', fontWeight:600,
                      color: form[key] ? '#15803d' : P.muted, transition:'all .15s',
                    }}>
                      <input type="checkbox" checked={!!form[key]} onChange={e=>setForm(f=>({...f,[key]:e.target.checked}))}
                        style={{ accentColor:'#15803d' }} />
                      {label}
                    </label>
                  ))}
                </div>
              </Field>
            </div>
            {form.num_iw && (
              <div style={{ gridColumn:'1/-1', background:'#ede9fe', borderRadius:8, padding:'8px 12px', fontSize:'0.8rem', color:'#6d28d9' }}>
                📋 Originado da Captação — Paciente nº IW <strong>{form.num_iw}</strong>{form.filial ? ` · ${form.filial === 'Homesenior' ? 'Vitalcare' : form.filial}` : ''}
              </div>
            )}
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Protocolo / Nº autorização">
                <input value={form.protocolo} onChange={e=>setForm(f=>({...f,protocolo:e.target.value}))} placeholder="Nº do protocolo ou autorização da operadora" style={inp} />
              </Field>
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Observações">
                <textarea value={form.observacoes} onChange={e=>setForm(f=>({...f,observacoes:e.target.value}))} rows={3} placeholder="Equipe de remoção, telefone de contato, nº do pedido…" style={{ ...inp, resize:'vertical' }} />
              </Field>
            </div>

            {/* Texto WhatsApp */}
            <div style={{ gridColumn:'1/-1', marginTop:4 }}>
              <button onClick={gerarTextoSolicitacao}
                style={{ background:'#25d366', color:'#fff', border:'none', borderRadius:8, padding:'7px 16px',
                  fontSize:'0.82rem', fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                📋 Texto para Solicitação (copiar)
              </button>
              {textoSolic && (
                <textarea
                  readOnly
                  value={textoSolic}
                  rows={12}
                  onClick={e=>{ e.target.select(); try{navigator.clipboard.writeText(textoSolic);}catch{} }}
                  style={{ ...inp, marginTop:8, fontFamily:'monospace', fontSize:'0.78rem',
                    background:'#f0fdf4', border:'1px solid #86efac', resize:'vertical', cursor:'pointer' }}
                />
              )}
            </div>
          </div>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.paciente.trim()}>{saving?'Salvando…':'Salvar remoção'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}
      {delId && (
        <Modal title="Confirmar exclusão" onClose={()=>setDelId(null)}>
          <p style={{ color:P.text, marginBottom:20 }}>Deseja excluir este registro? Esta ação não pode ser desfeita.</p>
          <div style={{ display:'flex', gap:10 }}>
            <Btn color={P.red} onClick={()=>{ deleteDoc(doc(db,col,delId)); setDelId(null); }}>Excluir</Btn>
            <GhostBtn onClick={()=>setDelId(null)}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}
      {statusModal && (
        <GerenciarStatusModal
          statusList={statusOptions.map(nome => {
            const c = statusColorMap[nome] || {};
            return { nome, corBg: c.bg, corText: c.text };
          })}
          onClose={()=>setStatusModal(false)}
        />
      )}
    </div>
  );
}

// ── Modal: Gerenciar Status (somente Admin) ────────────────────────────────────
function GerenciarStatusModal({ statusList, onClose }) {
  const [novoNome, setNovoNome] = useState('');
  const [novaCor,  setNovaCor]  = useState(0);
  const [saving,   setSaving]   = useState(false);
  const [delAlvo,  setDelAlvo]  = useState(null);
  const col = getCollectionName('rm_status_config');
  const ref = doc(db, col, 'status_agendadas');

  const salvarLista = async novaLista => {
    setSaving(true);
    try {
      await setDoc(ref, { statuses: novaLista, editadoEm: serverTimestamp() });
    } catch(e) { alert('Erro ao salvar: ' + e.message); }
    setSaving(false);
  };

  const adicionar = async () => {
    const nome = novoNome.trim();
    if (!nome) return;
    if (statusList.some(s => s.nome.toLowerCase() === nome.toLowerCase())) {
      alert('Já existe um status com esse nome.');
      return;
    }
    const cor = STATUS_PALETA[novaCor];
    await salvarLista([...statusList, { nome, corBg: cor.corBg, corText: cor.corText }]);
    setNovoNome('');
  };

  const remover = async nome => {
    await salvarLista(statusList.filter(s => s.nome !== nome));
    setDelAlvo(null);
  };

  return (
    <Modal title="⚙️ Gerenciar Status" onClose={onClose}>
      <p style={{ color:P.muted, fontSize:'0.82rem', marginBottom:14 }}>
        Adicione ou remova os tipos de status usados nas remoções. Remover um status não altera registros que já usam esse valor — só tira ele da lista de opções.
      </p>
      <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:16, maxHeight:280, overflowY:'auto' }}>
        {statusList.map(s => (
          <div key={s.nome} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
            padding:'6px 10px', borderRadius:8, border:`1px solid ${P.border}` }}>
            <StatusBadge status={s.nome} map={{ [s.nome]: { bg:s.corBg, text:s.corText } }} />
            {delAlvo === s.nome ? (
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <span style={{ fontSize:'0.72rem', color:P.red }}>Confirmar?</span>
                <Btn small color={P.red} onClick={()=>remover(s.nome)} disabled={saving}>Sim</Btn>
                <GhostBtn small onClick={()=>setDelAlvo(null)}>Não</GhostBtn>
              </div>
            ) : (
              <GhostBtn small onClick={()=>setDelAlvo(s.nome)}>🗑</GhostBtn>
            )}
          </div>
        ))}
      </div>
      <div style={{ borderTop:`1px solid ${P.border}`, paddingTop:14 }}>
        <Field label="Novo status">
          <input value={novoNome} onChange={e=>setNovoNome(e.target.value)} placeholder="Ex.: Em recurso" style={inp} />
        </Field>
        <div style={{ display:'flex', gap:6, marginBottom:14 }}>
          {STATUS_PALETA.map((c,i) => (
            <button key={i} onClick={()=>setNovaCor(i)} style={{
              width:26, height:26, borderRadius:'50%', background:c.corBg, cursor:'pointer',
              border: novaCor===i ? `2px solid ${c.corText}` : `1px solid ${P.border}`,
            }} title="Escolher cor" />
          ))}
        </div>
        <Btn onClick={adicionar} disabled={saving||!novoNome.trim()}>{saving?'Salvando…':'+ Adicionar status'}</Btn>
      </div>
    </Modal>
  );
}

// ── Tab 2: Para Agendar ───────────────────────────────────────────────────────
const buscarCepRem = async (cep) => {
  const c = cep.replace(/\D/g, '');
  if (c.length !== 8) return null;
  try {
    const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
    const d = await r.json();
    if (d.erro) return null;
    return [d.logradouro, d.bairro, d.localidade, d.uf].filter(Boolean).join(', ');
  } catch { return null; }
};

const EMPTY_PA = {
  paciente:'', plano:'', motivo:'',
  local_origem:'', local_destino:'',
  cep_origem:'', cep_destino:'',
  tipo_deslocamento:'so_ida',
  tem_medico:false, tem_o2:false,
  data_agendamento:'', horario:'',
  origem_solicitacao:'', observacao:'',
};

const ORIGENS_SOLIC = ['Solicitação Família','Solicitação Médica','Captação (automático)','Outro'];

function TabParaAgendar({ data, onConfirmar, canceladosMap, onlyImplantacao = false }) {
  const [search, setSearch] = useState('');
  const [fDIni,  setFDIni]  = useState('');
  const [fDFim,  setFDFim]  = useState('');
  const [modal, setModal]   = useState(false);
  const [edit,  setEdit]    = useState(null);
  const [form,  setForm]    = useState(EMPTY_PA);
  const [saving,setSaving]  = useState(false);
  const col = getCollectionName('rm_para_agendar');

  const openNew  = () => { setForm(EMPTY_PA); setEdit(null); setModal(true); };
  const openEdit = r  => { setForm({...EMPTY_PA,...r}); setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const save = async () => {
    if (!form.paciente.trim()) return;
    setSaving(true);
    try {
      if (edit) await updateDoc(doc(db, col, edit.id), { ...form, updatedAt: serverTimestamp() });
      else      await addDoc(collection(db, col), { ...form, criadoEm: serverTimestamp() });
      close();
    } catch(e) { console.error(e); alert('Erro ao salvar: ' + (e.message || JSON.stringify(e))); }
    setSaving(false);
  };

  const rows = useMemo(() => {
    let r = data.filter(x => onlyImplantacao ? x.origem_fluxo === 'captacao_previa' : x.origem_fluxo !== 'captacao_previa');
    if (search) r = r.filter(x => (x.paciente+x.motivo).toLowerCase().includes(search.toLowerCase()));
    if (fDIni)  r = r.filter(x => x.data_agendamento && x.data_agendamento >= fDIni);
    if (fDFim)  r = r.filter(x => x.data_agendamento && x.data_agendamento <= fDFim);
    return r;
  }, [data, search, fDIni, fDFim, onlyImplantacao]);

  const hasF = search || fDIni || fDFim;
  const clrF = () => { setSearch(''); setFDIni(''); setFDFim(''); };

  return (
    <div>
      <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:10, padding:'10px 16px', marginBottom:18, fontSize:'0.82rem', color:'#92400e', fontWeight:600 }}>
        {rows.length} solicitação{rows.length!==1?'es':''} aguardando agendamento{onlyImplantacao ? ' (implantação)' : ''}
      </div>
      <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        {!onlyImplantacao && <Btn onClick={openNew}>+ Nova Solicitação</Btn>}
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar…" style={{ ...inp, maxWidth:260 }} />
        <span style={{ fontSize:'0.75rem', color:P.muted }}>Agendamento:</span>
        <input type="date" value={fDIni} onChange={e=>setFDIni(e.target.value)} style={{ ...inp, maxWidth:148 }} title="Agendamento de" />
        <input type="date" value={fDFim} onChange={e=>setFDFim(e.target.value)} style={{ ...inp, maxWidth:148 }} title="Agendamento até" />
        {hasF && <GhostBtn onClick={clrF}>✕ Limpar</GhostBtn>}
        <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:P.muted }}>{rows.length} registro{rows.length!==1?'s':''}</span>
      </div>
      <div style={{ overflowX:'auto', background:P.white, borderRadius:12, border:`1px solid ${P.border}` }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.8rem' }}>
          <thead><tr>
            <TH>Origem Sol.</TH><TH>Prévia</TH>
            <TH>Paciente</TH><TH>Plano</TH><TH>Motivo</TH>
            <TH>Origem</TH><TH>Destino</TH><TH>Detalhes</TH>
            <TH>Agendamento</TH><TH>Ações</TH>
          </tr></thead>
          <tbody>
            {rows.length===0 && <tr><td colSpan={10} style={{ padding:32, textAlign:'center', color:P.muted }}>Fila vazia.</td></tr>}
            {rows.map((r,i)=>{
              const cancelada = isCaptacaoCancelada(r, canceladosMap);
              return (
              <tr key={r.id} className={cancelada ? 'rm-cancelada' : undefined} style={{ borderBottom:`1px solid #f1f5f9`, background:cancelada?'#111827':(i%2===0?'transparent':'#fafafa') }}>
                {/* Origem da Solicitação */}
                <td style={{ padding:'9px 12px', whiteSpace:'nowrap' }}>
                  {r.origem==='IW' && (
                    <div style={{ marginBottom:3 }}>
                      <span style={{ background:'#ecfdf5', color:'#065f46', border:'1px solid #a7f3d0', fontSize:'0.7rem', fontWeight:700, padding:'1px 7px', borderRadius:20 }}
                        title={`Pedido no IW em ${fmtDate(r.iw_data_pedido) || '—'}${r.iw_profissional ? ` por ${r.iw_profissional}` : ''} · lançado sozinho pelo robô`}>🗂 Veio do IW</span>
                    </div>
                  )}
                  {r.origem_fluxo==='captacao_previa'
                    ? <span style={{ background:'#ede9fe', color:'#7c3aed', fontSize:'0.72rem', fontWeight:700, padding:'2px 8px', borderRadius:20 }}>Captação</span>
                    : r.origem_solicitacao
                      ? <span style={{ background:'#f0f9ff', color:'#0369a1', fontSize:'0.72rem', fontWeight:600, padding:'2px 8px', borderRadius:20 }}>{r.origem_solicitacao}</span>
                      : <span style={{ color:P.muted, fontSize:'0.72rem' }}>—</span>
                  }
                </td>
                {/* Prévia */}
                <td style={{ padding:'9px 12px', textAlign:'center' }}>
                  {r.origem_fluxo==='captacao_previa'
                    ? <span style={{ background:'#ede9fe', color:'#7c3aed', fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', borderRadius:20, whiteSpace:'nowrap' }} title={`Nº IW: ${r.num_iw||'—'}`}>🗒 Prévia</span>
                    : <span style={{ color:'#cbd5e1' }}>—</span>}
                </td>
                <td style={{ padding:'9px 12px', fontWeight:600, color:P.text }}>
                  {r.paciente}
                  {r.num_iw && <span style={{ marginLeft:6, color:P.muted, fontSize:'0.72rem' }}>#{r.num_iw}</span>}
                </td>
                <td style={{ padding:'9px 12px' }}><OpBadge op={r.plano} /></td>
                <td style={{ padding:'9px 12px', color:P.text, maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.origem==='IW' ? r.observacao : r.motivo}>{r.motivo||'—'}</td>
                <td style={{ padding:'9px 12px', color:P.muted, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.local_origem||r.local}>
                  {r.local_origem||r.local||'—'}
                  {r.cep_origem&&<span style={{color:'#94a3b8',marginLeft:4,fontSize:'0.68rem'}}>({r.cep_origem})</span>}
                </td>
                <td style={{ padding:'9px 12px', color:P.muted, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.local_destino}>
                  {r.local_destino||'—'}
                  {r.cep_destino&&<span style={{color:'#94a3b8',marginLeft:4,fontSize:'0.68rem'}}>({r.cep_destino})</span>}
                </td>
                <td style={{ padding:'9px 12px', whiteSpace:'nowrap', fontSize:'0.75rem' }}>
                  {r.tipo_deslocamento==='ida_e_volta'&&<span style={{ background:'#dbeafe', color:'#1d4ed8', borderRadius:4, padding:'1px 5px', marginRight:3 }}>↔ Ida/Volta</span>}
                  {r.tem_medico&&<span style={{ background:'#dcfce7', color:'#15803d', borderRadius:4, padding:'1px 5px', marginRight:3 }}>🩺 Médico</span>}
                  {r.tem_o2&&<span style={{ background:'#fef9c3', color:'#92400e', borderRadius:4, padding:'1px 5px' }}>O₂</span>}
                </td>
                <td style={{ padding:'9px 12px', color:P.text, whiteSpace:'nowrap' }}>
                  <div>{fmtDate(r.data_agendamento) || r.data_agendamento || '—'}</div>
                  {r.horario && <div style={{ color:P.muted, fontSize:'0.7rem' }}>{r.horario}</div>}
                </td>
                <td style={{ padding:'9px 12px', whiteSpace:'nowrap' }}>
                  <div style={{ display:'flex', gap:6 }}>
                    <Btn small color={P.green} onClick={()=>onConfirmar&&onConfirmar(r)}>✔ Confirmar</Btn>
                    <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
                    <GhostBtn small onClick={()=>deleteDoc(doc(db,col,r.id))}>🗑</GhostBtn>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {modal && (
        <Modal title={edit?'Editar Solicitação':'Nova Solicitação'} onClose={close}>
          <Field label="Paciente" required><input value={form.paciente} onChange={e=>setForm(f=>({...f,paciente:e.target.value}))} style={inp} /></Field>
          <Field label="Plano"><Sel value={form.plano} onChange={v=>setForm(f=>({...f,plano:v}))} options={OPERADORAS} placeholder="Selecionar…" /></Field>
          <Field label="Motivo"><input value={form.motivo} onChange={e=>setForm(f=>({...f,motivo:e.target.value}))} placeholder="Ex.: Desospitalização" style={inp} /></Field>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 140px', gap:8 }}>
            <Field label="Local de Origem">
              <input value={form.local_origem} onChange={e=>setForm(f=>({...f,local_origem:e.target.value}))} placeholder="Hospital / endereço de origem" style={inp} />
            </Field>
            <Field label="CEP de Origem">
              <input value={form.cep_origem} maxLength={9} placeholder="00000-000" style={inp}
                onChange={async e=>{ setForm(f=>({...f,cep_origem:e.target.value})); const end=await buscarCepRem(e.target.value); if(end) setForm(f=>({...f,local_origem:end})); }} />
            </Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 140px', gap:8 }}>
            <Field label="Local de Destino">
              <input value={form.local_destino} onChange={e=>setForm(f=>({...f,local_destino:e.target.value}))} placeholder="Residência / endereço de destino" style={inp} />
            </Field>
            <Field label="CEP de Destino">
              <input value={form.cep_destino} maxLength={9} placeholder="00000-000" style={inp}
                onChange={async e=>{ setForm(f=>({...f,cep_destino:e.target.value})); const end=await buscarCepRem(e.target.value); if(end) setForm(f=>({...f,local_destino:end})); }} />
            </Field>
          </div>
          <Field label="Tipo de Deslocamento">
            <div style={{ display:'flex', gap:8 }}>
              {[['so_ida','→ Só Ida'],['ida_e_volta','↔ Ida e Volta']].map(([v,l])=>(
                <button key={v} type="button" onClick={()=>setForm(f=>({...f,tipo_deslocamento:v}))}
                  style={{ flex:1, padding:'8px', borderRadius:7, border:`2px solid ${form.tipo_deslocamento===v?P.primary:P.border}`, background:form.tipo_deslocamento===v?`${P.primary}15`:P.white, color:form.tipo_deslocamento===v?P.primary:P.muted, fontWeight:700, cursor:'pointer' }}>
                  {l}
                </button>
              ))}
            </div>
          </Field>
          <div style={{ display:'flex', gap:16, marginBottom:6 }}>
            <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontWeight:600, fontSize:'0.82rem', color:P.text }}>
              <input type="checkbox" checked={form.tem_medico} onChange={e=>setForm(f=>({...f,tem_medico:e.target.checked}))} />
              🩺 Médico acompanhante
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontWeight:600, fontSize:'0.82rem', color:P.text }}>
              <input type="checkbox" checked={form.tem_o2} onChange={e=>setForm(f=>({...f,tem_o2:e.target.checked}))} />
              O₂ necessário
            </label>
          </div>
          <Field label="Origem da Solicitação">
            <select value={form.origem_solicitacao} onChange={e=>setForm(f=>({...f,origem_solicitacao:e.target.value}))} style={inp}>
              <option value="">— Selecionar —</option>
              {ORIGENS_SOLIC.map(o=><option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            <Field label="Data do agendamento">
              <input type="date" value={form.data_agendamento} onChange={e=>setForm(f=>({...f,data_agendamento:e.target.value}))} style={inp} />
            </Field>
            <Field label="Horário">
              <input type="time" value={form.horario} onChange={e=>setForm(f=>({...f,horario:e.target.value}))} style={inp} />
            </Field>
          </div>
          <Field label="Observação"><textarea value={form.observacao} onChange={e=>setForm(f=>({...f,observacao:e.target.value}))} rows={2} style={{ ...inp, resize:'vertical' }} /></Field>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.paciente.trim()}>{saving?'Salvando…':'Salvar'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab: Dashboard de Valores ───────────────────────────────────────────────────
function TabDashboardValores({ data }) {
  const [fDIni, setFDIni] = useState('');
  const [fDFim, setFDFim] = useState('');

  const validas = useMemo(() => {
    let r = (data || []).filter(x => !CANCELADOS_STATUS.includes(x.status));
    if (fDIni) r = r.filter(x => x.data >= fDIni);
    if (fDFim) r = r.filter(x => x.data <= fDFim);
    return r;
  }, [data, fDIni, fDFim]);

  const numOk = v => v !== '' && v != null && !isNaN(Number(v));
  const comValor = useMemo(() => validas.filter(r => numOk(r.valorCotado) || numOk(r.valorCobrado)), [validas]);

  const totais = useMemo(() => {
    let totalCotado = 0, realPago = 0, cobertoAditivo = 0, totalCobrado = 0;
    comValor.forEach(r => {
      const vc = numOk(r.valorCotado) ? Number(r.valorCotado) : 0;
      totalCotado += vc;
      if (r.aditivo_iw === 'Sim') cobertoAditivo += vc; else realPago += vc;
      if (numOk(r.valorCobrado)) totalCobrado += Number(r.valorCobrado);
    });
    return { totalCotado, realPago, cobertoAditivo, totalCobrado };
  }, [comValor]);

  const porEmpresa = useMemo(() => {
    const map = {};
    comValor.forEach(r => {
      const k = r.empresaRemocao || '(sem empresa)';
      if (!map[k]) map[k] = { qtd:0, cotado:0, realPago:0, cobrado:0 };
      const vc = numOk(r.valorCotado) ? Number(r.valorCotado) : 0;
      map[k].qtd++; map[k].cotado += vc;
      if (r.aditivo_iw !== 'Sim') map[k].realPago += vc;
      if (numOk(r.valorCobrado)) map[k].cobrado += Number(r.valorCobrado);
    });
    return Object.entries(map).sort((a,b) => b[1].cotado - a[1].cotado);
  }, [comValor]);

  const porMes = useMemo(() => {
    const map = {};
    comValor.forEach(r => {
      const k = toSortDate(r.data).slice(0,7) || '(sem data)';
      if (!map[k]) map[k] = { qtd:0, cotado:0, realPago:0, cobrado:0 };
      const vc = numOk(r.valorCotado) ? Number(r.valorCotado) : 0;
      map[k].qtd++; map[k].cotado += vc;
      if (r.aditivo_iw !== 'Sim') map[k].realPago += vc;
      if (numOk(r.valorCobrado)) map[k].cobrado += Number(r.valorCobrado);
    });
    return Object.entries(map).sort((a,b) => a[0].localeCompare(b[0]));
  }, [comValor]);

  return (
    <div>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:18 }}>
        <span style={{ fontSize:'0.78rem', color:P.muted, fontWeight:600 }}>Período:</span>
        <input type="date" value={fDIni} onChange={e=>setFDIni(e.target.value)} style={{ ...inp, maxWidth:148 }} />
        <input type="date" value={fDFim} onChange={e=>setFDFim(e.target.value)} style={{ ...inp, maxWidth:148 }} />
        {(fDIni||fDFim) && <GhostBtn onClick={()=>{setFDIni('');setFDFim('');}}>✕ Limpar</GhostBtn>}
        <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:P.muted }}>
          {comValor.length} de {validas.length} remoções com valor informado
        </span>
      </div>

      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:24 }}>
        <KpiCard label="Valor Total Cotado" value={fmtMoeda(totais.totalCotado)} color={P.blue} />
        <KpiCard label="💰 Valor Real Pago (sem aditivo)" value={fmtMoeda(totais.realPago)} color="#b91c1c" />
        <KpiCard label="📄 Coberto por Aditivo no IW" value={fmtMoeda(totais.cobertoAditivo)} color={P.green} />
        <KpiCard label="🏦 Total Cobrado à Operadora" value={fmtMoeda(totais.totalCobrado)} color="#7c3aed" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, padding:16 }}>
          <div style={{ fontWeight:700, fontSize:'0.82rem', color:P.text, marginBottom:10 }}>Por Empresa da Remoção</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
            <thead><tr><TH>Empresa</TH><TH>Qtd.</TH><TH>Cotado</TH><TH>Real pago</TH><TH>Cobrado Operadora</TH></tr></thead>
            <tbody>
              {porEmpresa.length === 0 && <tr><td colSpan={5} style={{ padding:20, textAlign:'center', color:P.muted }}>Sem dados.</td></tr>}
              {porEmpresa.map(([nome, v]) => (
                <tr key={nome} style={{ borderBottom:'1px solid #f1f5f9' }}>
                  <td style={{ padding:'7px 8px', color:P.text, fontWeight:600 }}>{nome}</td>
                  <td style={{ padding:'7px 8px', color:P.muted }}>{v.qtd}</td>
                  <td style={{ padding:'7px 8px', color:P.text }}>{fmtMoeda(v.cotado)}</td>
                  <td style={{ padding:'7px 8px', color:'#b91c1c' }}>{fmtMoeda(v.realPago)}</td>
                  <td style={{ padding:'7px 8px', color:'#7c3aed' }}>{fmtMoeda(v.cobrado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, padding:16 }}>
          <div style={{ fontWeight:700, fontSize:'0.82rem', color:P.text, marginBottom:10 }}>Por Mês</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
            <thead><tr><TH>Mês</TH><TH>Qtd.</TH><TH>Cotado</TH><TH>Real pago</TH><TH>Cobrado Operadora</TH></tr></thead>
            <tbody>
              {porMes.length === 0 && <tr><td colSpan={5} style={{ padding:20, textAlign:'center', color:P.muted }}>Sem dados.</td></tr>}
              {porMes.map(([mes, v]) => (
                <tr key={mes} style={{ borderBottom:'1px solid #f1f5f9' }}>
                  <td style={{ padding:'7px 8px', color:P.text, fontWeight:600 }}>{mes === '(sem data)' ? mes : mes.split('-').reverse().join('/')}</td>
                  <td style={{ padding:'7px 8px', color:P.muted }}>{v.qtd}</td>
                  <td style={{ padding:'7px 8px', color:P.text }}>{fmtMoeda(v.cotado)}</td>
                  <td style={{ padding:'7px 8px', color:'#b91c1c' }}>{fmtMoeda(v.realPago)}</td>
                  <td style={{ padding:'7px 8px', color:'#7c3aed' }}>{fmtMoeda(v.cobrado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Cancelados (remoções vinculadas a paciente cancelado na Captação) ─────
function TabCancelados({ agendadas, paraAgendar, canceladosMap }) {
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    const ag = (agendadas || [])
      .filter(r => isCaptacaoCancelada(r, canceladosMap) || r.status === 'Cancelado')
      .map(r => ({ ...r, _origem: 'Agendada' }));
    const pa = (paraAgendar || [])
      .filter(r => isCaptacaoCancelada(r, canceladosMap))
      .map(r => ({ ...r, _origem: 'Para Agendar' }));
    let all = [...ag, ...pa];
    if (search) all = all.filter(x => (x.paciente || '').toLowerCase().includes(search.toLowerCase()));
    return all;
  }, [agendadas, paraAgendar, canceladosMap, search]);

  return (
    <div>
      <div style={{ background:'#111827', color:'#e2e8f0', borderRadius:10, padding:'10px 16px', marginBottom:18, fontSize:'0.82rem', fontWeight:600 }}>
        ⚫ {rows.length} remoç{rows.length !== 1 ? 'ões canceladas' : 'ão cancelada'}
      </div>
      <div style={{ display:'flex', gap:10, marginBottom:14 }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar paciente…" style={{ ...inp, maxWidth:260 }} />
      </div>
      <div style={{ overflowX:'auto', background:P.white, borderRadius:12, border:`1px solid ${P.border}` }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.8rem' }}>
          <thead><tr>
            <TH>Paciente</TH><TH>Origem</TH><TH>Operadora / Plano</TH><TH>Motivo</TH>
            <TH>Status</TH><TH>Cancelado em</TH>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhuma remoção cancelada.</td></tr>}
            {rows.map(r => {
              const canceladoEm = r.captacao_id && canceladosMap?.[r.captacao_id]?.canceladoEm;
              return (
                <tr key={r.id} className="rm-cancelada" style={{ background:'#111827', borderBottom:'1px solid #1f2937' }}>
                  <td style={{ padding:'9px 12px', fontWeight:600 }}>
                    {r.paciente}
                    {r.num_iw && <span style={{ marginLeft:6, color:'#94a3b8', fontSize:'0.72rem' }}>#{r.num_iw}</span>}
                  </td>
                  <td style={{ padding:'9px 12px' }}>{r._origem}</td>
                  <td style={{ padding:'9px 12px' }}>{r.operadora || r.plano || '—'}</td>
                  <td style={{ padding:'9px 12px' }}>{r.motivo || '—'}</td>
                  <td style={{ padding:'9px 12px' }}><StatusBadge status={r.status || 'Cancelado'} small /></td>
                  <td style={{ padding:'9px 12px' }}>{canceladoEm ? new Date(canceladoEm).toLocaleString('pt-BR') : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab 3: Contato Médico ─────────────────────────────────────────────────────
const STATUS_CONT = ['Pendente','Contato realizado','Sem retorno'];
const EMPTY_CONT  = { paciente:'', medico:'', motivo:'', telefone:'', status:'Pendente', observacoes:'' };

// Fora das abas desde 14/10 (contato médico saiu; cânulas foram para Dispositivos) — guardado caso volte
// eslint-disable-next-line no-unused-vars
function TabContatoMedico({ data }) {
  const [form,  setForm]  = useState(EMPTY_CONT);
  const [edit,  setEdit]  = useState(null);
  const [modal, setModal] = useState(false);
  const [saving,setSaving]= useState(false);
  const col = getCollectionName('rm_contato');

  const openNew  = () => { setForm(EMPTY_CONT); setEdit(null); setModal(true); };
  const openEdit = r  => { setForm({...EMPTY_CONT,...r}); setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const save = async () => {
    if (!form.paciente.trim()) return;
    setSaving(true);
    try {
      if (edit) await updateDoc(doc(db, col, edit.id), { ...form, updatedAt: serverTimestamp() });
      else      await addDoc(collection(db, col), { ...form, criadoEm: serverTimestamp() });
      close();
    } catch(e) { console.error(e); alert('Erro ao salvar: ' + (e.message || JSON.stringify(e))); }
    setSaving(false);
  };

  const markDone = r => updateDoc(doc(db, col, r.id), { status:'Contato realizado', updatedAt: serverTimestamp() });

  const { pendentes, realizados } = useMemo(() => ({
    pendentes:  data.filter(x => x.status === 'Pendente'),
    realizados: data.filter(x => x.status !== 'Pendente'),
  }), [data]);

  const Row = ({ r, i }) => (
    <tr style={{ borderBottom:`1px solid #f1f5f9`, background:i%2===0?'transparent':'#fafafa' }}>
      <td style={{ padding:'9px 12px', fontWeight:600 }}>{r.paciente}</td>
      <td style={{ padding:'9px 12px', color:P.muted }}>{r.medico||'—'}</td>
      <td style={{ padding:'9px 12px' }}>{r.motivo||'—'}</td>
      <td style={{ padding:'9px 12px', color:P.muted }}>{r.telefone||'—'}</td>
      <td style={{ padding:'9px 12px' }}><StatusBadge status={r.status} /></td>
      <td style={{ padding:'9px 12px', color:P.muted, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.observacoes||'—'}</td>
      <td style={{ padding:'9px 12px' }}>
        <div style={{ display:'flex', gap:6 }}>
          {r.status==='Pendente' && <Btn small color={P.green} onClick={()=>markDone(r)}>✔ Realizado</Btn>}
          <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
          <GhostBtn small onClick={()=>deleteDoc(doc(db,col,r.id))}>🗑</GhostBtn>
        </div>
      </td>
    </tr>
  );

  return (
    <div>
      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <Btn onClick={openNew}>+ Novo Contato</Btn>
        <span style={{ marginLeft:'auto', fontSize:'0.78rem', color:P.muted, alignSelf:'center' }}>
          {pendentes.length} pendente{pendentes.length!==1?'s':''} · {realizados.length} realizado{realizados.length!==1?'s':''}
        </span>
      </div>
      <div style={{ overflowX:'auto', background:P.white, borderRadius:12, border:`1px solid ${P.border}` }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.8rem' }}>
          <thead><tr><TH>Paciente</TH><TH>Médico</TH><TH>Motivo</TH><TH>Telefone</TH><TH>Status</TH><TH>Observações</TH><TH>Ações</TH></tr></thead>
          <tbody>
            {data.length===0 && <tr><td colSpan={7} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum contato registrado.</td></tr>}
            {pendentes.map((r,i)  => <Row key={r.id} r={r} i={i} />)}
            {realizados.map((r,i) => <Row key={r.id} r={r} i={i} />)}
          </tbody>
        </table>
      </div>
      {modal && (
        <Modal title={edit?'Editar Contato':'Novo Contato'} onClose={close}>
          <Field label="Paciente" required><input value={form.paciente} onChange={e=>setForm(f=>({...f,paciente:e.target.value}))} style={inp} /></Field>
          <Field label="Médico / Especialidade"><input value={form.medico} onChange={e=>setForm(f=>({...f,medico:e.target.value}))} placeholder="Ex.: Dr. João — Pneumologia" style={inp} /></Field>
          <Field label="Motivo"><input value={form.motivo} onChange={e=>setForm(f=>({...f,motivo:e.target.value}))} style={inp} /></Field>
          <Field label="Telefone"><input value={form.telefone} onChange={e=>setForm(f=>({...f,telefone:e.target.value}))} style={inp} /></Field>
          <Field label="Status"><Sel value={form.status} onChange={v=>setForm(f=>({...f,status:v}))} options={STATUS_CONT} /></Field>
          <Field label="Observações"><textarea value={form.observacoes} onChange={e=>setForm(f=>({...f,observacoes:e.target.value}))} rows={2} style={{ ...inp, resize:'vertical' }} /></Field>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.paciente.trim()}>{saving?'Salvando…':'Salvar'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab 4: Seriadas ───────────────────────────────────────────────────────────
const EMPTY_SER = { paciente:'', motivo:'', operadora:'', enviado_unimed:'Não', mes:'' };

function TabSeriadas({ data }) {
  const [mesF,  setMesF]  = useState('');
  const [form,  setForm]  = useState(EMPTY_SER);
  const [edit,  setEdit]  = useState(null);
  const [modal, setModal] = useState(false);
  const [saving,setSaving]= useState(false);
  const col = getCollectionName('rm_seriadas');

  const openNew  = () => { setForm(EMPTY_SER); setEdit(null); setModal(true); };
  const openEdit = r  => { setForm({...EMPTY_SER,...r}); setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const save = async () => {
    if (!form.paciente.trim()) return;
    setSaving(true);
    try {
      if (edit) await updateDoc(doc(db, col, edit.id), { ...form, updatedAt: serverTimestamp() });
      else      await addDoc(collection(db, col), { ...form, criadoEm: serverTimestamp() });
      close();
    } catch(e) { console.error(e); alert('Erro ao salvar: ' + (e.message || JSON.stringify(e))); }
    setSaving(false);
  };

  const toggleEnviado = r => updateDoc(doc(db, col, r.id), { enviado_unimed: r.enviado_unimed==='Sim'?'Não':'Sim', updatedAt: serverTimestamp() });

  const renovar = async () => {
    const atual = mesF ? data.filter(x => x.mes === mesF) : data;
    for (const item of atual) {
      const idx = MES_OPTS.indexOf(item.mes);
      const novoMes = MES_OPTS[(idx+1) % 12] || '';
      const { id: _id, ...rest } = item;
      await addDoc(collection(db, col), { ...rest, mes:novoMes, enviado_unimed:'Não', criadoEm:serverTimestamp() });
    }
  };

  const rows = useMemo(() => mesF ? data.filter(x => x.mes === mesF) : data, [data, mesF]);

  return (
    <div>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
        <Btn onClick={openNew}>+ Nova Remoção Seriada</Btn>
        <select value={mesF} onChange={e=>setMesF(e.target.value)} style={{ ...inp, maxWidth:160 }}>
          <option value="">Todos os meses</option>
          {MES_OPTS.map(m=><option key={m}>{m}</option>)}
        </select>
        <Btn color={P.amber} onClick={renovar}>↻ Renovar para próximo mês</Btn>
      </div>
      <div style={{ overflowX:'auto', background:P.white, borderRadius:12, border:`1px solid ${P.border}` }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.8rem' }}>
          <thead><tr><TH>Paciente</TH><TH>Motivo</TH><TH>Operadora</TH><TH>Enviado à Unimed?</TH><TH>Mês</TH><TH>Ações</TH></tr></thead>
          <tbody>
            {rows.length===0 && <tr><td colSpan={6} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum registro.</td></tr>}
            {rows.map((r,i)=>(
              <tr key={r.id} style={{ borderBottom:`1px solid #f1f5f9`, background:i%2===0?'transparent':'#fafafa' }}>
                <td style={{ padding:'9px 12px', fontWeight:600 }}>{r.paciente}</td>
                <td style={{ padding:'9px 12px', color:P.text }}>{r.motivo||'—'}</td>
                <td style={{ padding:'9px 12px' }}><OpBadge op={r.operadora} /></td>
                <td style={{ padding:'9px 12px' }}>
                  <span onClick={()=>toggleEnviado(r)} style={{ cursor:'pointer',
                    background:r.enviado_unimed==='Sim'?'#dcfce7':'#fee2e2',
                    color:r.enviado_unimed==='Sim'?'#15803d':'#b91c1c',
                    padding:'3px 12px', borderRadius:20, fontWeight:700, fontSize:'0.74rem' }}>
                    {r.enviado_unimed||'Não'}
                  </span>
                </td>
                <td style={{ padding:'9px 12px', color:P.muted }}>{r.mes||'—'}</td>
                <td style={{ padding:'9px 12px' }}>
                  <div style={{ display:'flex', gap:6 }}>
                    <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
                    <GhostBtn small onClick={()=>deleteDoc(doc(db,col,r.id))}>🗑</GhostBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && (
        <Modal title={edit?'Editar Seriada':'Nova Seriada'} onClose={close}>
          <Field label="Paciente" required><input value={form.paciente} onChange={e=>setForm(f=>({...f,paciente:e.target.value}))} style={inp} /></Field>
          <Field label="Motivo"><input value={form.motivo} onChange={e=>setForm(f=>({...f,motivo:e.target.value}))} placeholder="Ex.: Terapia mensal" style={inp} /></Field>
          <Field label="Operadora"><Sel value={form.operadora} onChange={v=>setForm(f=>({...f,operadora:v}))} options={OPERADORAS} placeholder="Selecionar…" /></Field>
          <Field label="Enviado à Unimed?"><Sel value={form.enviado_unimed} onChange={v=>setForm(f=>({...f,enviado_unimed:v}))} options={['Sim','Não']} /></Field>
          <Field label="Mês de referência"><Sel value={form.mes} onChange={v=>setForm(f=>({...f,mes:v}))} options={MES_OPTS} placeholder="Selecionar…" /></Field>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.paciente.trim()}>{saving?'Salvando…':'Salvar'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tabs 5-8: Cânulas / Sondas ───────────────────────────────────────────────
// Fora das abas desde 14/10 (contato médico saiu; cânulas foram para Dispositivos) — guardado caso volte
// eslint-disable-next-line no-unused-vars
const CANULA_CONFIGS = {
  unimed_outros: {
    cols:   ['Paciente','Médica','Regional','Cânula'],
    fields: [
      { key:'paciente',      label:'Paciente',           type:'text',   bold:true, required:true },
      { key:'medica',        label:'Médica responsável', type:'text' },
      { key:'regional',      label:'Regional Unimed',    type:'select', options:UNIMED_OUTROS_OPTS },
      { key:'canula',        label:'Modelo da Cânula',   type:'text',   placeholder:'Ex.: Shiley 5,5 com cuff' },
      { key:'ultima_troca',  label:'Última troca',       type:'date' },
      { key:'periodicidade', label:'Periodicidade',      type:'select', options:PERIODICIDADE_OPTS },
    ],
  },
  bradesco: {
    banner: 'BRADESCO: a mãe é quem agenda — exceto a paciente Angel, que faz a troca de cânula no CPL.',
    cols:   ['Paciente','Cânula'],
    fields: [
      { key:'paciente',      label:'Paciente',           type:'text', bold:true, required:true },
      { key:'canula',        label:'Modelo da Cânula',   type:'text', placeholder:'Ex.: Shiley 4,5 C/CUFF' },
      { key:'ultima_troca',  label:'Última troca',       type:'date' },
    ],
  },
};

// Fora das abas desde 14/10 (contato médico saiu; cânulas foram para Dispositivos) — guardado caso volte
// eslint-disable-next-line no-unused-vars
function TabCanulas({ data, tipo, config }) {
  const [search, setSearch]   = useState('');
  const [fStatus,setFStatus]  = useState('');
  const [form,   setForm]     = useState({});
  const [edit,   setEdit]     = useState(null);
  const [modal,  setModal]    = useState(false);
  const [saving, setSaving]   = useState(false);
  const col = getCollectionName('rm_canulas');

  const emptyForm = useMemo(() => config.fields.reduce((a,f) => ({ ...a, [f.key]:'' }), { tipo }), [tipo, config]);
  const openNew  = () => { setForm(emptyForm); setEdit(null); setModal(true); };
  const openEdit = r  => { setForm({ ...emptyForm, ...r }); setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const save = async () => {
    if (!form.paciente?.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.ultima_troca && payload.periodicidade)
        payload.proxima_troca = calcProxima(payload.ultima_troca, payload.periodicidade);
      payload.status_calculado = calcStatusCanula(payload.proxima_troca);
      if (edit) await updateDoc(doc(db, col, edit.id), { ...payload, updatedAt: serverTimestamp() });
      else      await addDoc(collection(db, col), { ...payload, tipo, criadoEm: serverTimestamp() });
      close();
    } catch(e) { console.error(e); alert('Erro ao salvar: ' + (e.message || JSON.stringify(e))); }
    setSaving(false);
  };

  const registrarTroca = async r => {
    const hoje = todayISO();
    const prox = r.periodicidade ? calcProxima(hoje, r.periodicidade) : '';
    await updateDoc(doc(db, col, r.id), {
      ultima_troca: hoje, proxima_troca: prox,
      status_calculado: calcStatusCanula(prox),
      updatedAt: serverTimestamp(),
    });
  };

  const rows = useMemo(() => {
    let r = data.filter(x => x.tipo === tipo);
    if (search)  r = r.filter(x => x.paciente?.toLowerCase().includes(search.toLowerCase()));
    if (fStatus) r = r.filter(x => (x.status_calculado || calcStatusCanula(x.proxima_troca)) === fStatus);
    return [...r].sort((a,b) => (a.proxima_troca||'9').localeCompare(b.proxima_troca||'9'));
  }, [data, tipo, search, fStatus]);

  const all  = data.filter(x => x.tipo === tipo);
  const kpis = useMemo(() => ({
    atrasado: all.filter(x => (x.status_calculado||calcStatusCanula(x.proxima_troca))==='Atrasado').length,
    proximo:  all.filter(x => (x.status_calculado||calcStatusCanula(x.proxima_troca))==='Próximo').length,
    emDia:    all.filter(x => (x.status_calculado||calcStatusCanula(x.proxima_troca))==='Em dia').length,
    total:    all.length,
  }), [all]);

  return (
    <div>
      {config.banner && (
        <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:10, padding:'10px 16px', marginBottom:18, fontSize:'0.84rem', color:'#92400e', fontWeight:600 }}>
          ⚠️ {config.banner}
        </div>
      )}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:18 }}>
        <KpiCard label="Atrasados" value={kpis.atrasado} color={P.red}   />
        <KpiCard label="Próximos"  value={kpis.proximo}  color={P.amber} />
        <KpiCard label="Em dia"    value={kpis.emDia}    color={P.green} />
        <KpiCard label="Total"     value={kpis.total}    color={P.muted} />
      </div>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
        <Btn onClick={openNew}>+ Novo Paciente</Btn>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar paciente…" style={{ ...inp, maxWidth:240 }} />
        <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{ ...inp, maxWidth:160 }}>
          <option value="">Todos os status</option>
          {Object.keys(STATUS_CANULA).map(s=><option key={s}>{s}</option>)}
        </select>
        {(search||fStatus) && <GhostBtn onClick={()=>{setSearch('');setFStatus('');}}>✕ Limpar</GhostBtn>}
      </div>
      <div style={{ overflowX:'auto', background:P.white, borderRadius:12, border:`1px solid ${P.border}` }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.8rem' }}>
          <thead><tr>
            {config.cols.map(h => <TH key={h}>{h}</TH>)}
            <TH>Última Troca</TH><TH>Periodicidade</TH><TH>Próxima Troca</TH><TH>Troca Prevista</TH><TH>Status</TH><TH>Ações</TH>
          </tr></thead>
          <tbody>
            {rows.length===0 && <tr><td colSpan={config.cols.length+6} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum registro.</td></tr>}
            {rows.map((r,i) => {
              const st = r.status_calculado || calcStatusCanula(r.proxima_troca);
              return (
                <tr key={r.id} style={{ borderBottom:`1px solid #f1f5f9`,
                  background:st==='Atrasado'?'#fff5f5':st==='Próximo'?'#fffbeb':i%2===0?'transparent':'#fafafa' }}>
                  {config.fields.filter(f=>config.cols.includes(f.label)).map(f=>(
                    <td key={f.key} style={{ padding:'9px 12px', color:f.bold?P.text:P.muted,
                      fontWeight:f.bold?600:400, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {r[f.key]||'—'}
                    </td>
                  ))}
                  <td style={{ padding:'9px 12px', color:P.muted, whiteSpace:'nowrap' }}>{fmtDate(r.ultima_troca)}</td>
                  <td style={{ padding:'9px 12px', color:P.muted, whiteSpace:'nowrap' }}>{r.periodicidade||'—'}</td>
                  <td style={{ padding:'9px 12px', whiteSpace:'nowrap', fontWeight:600,
                    color:st==='Atrasado'?P.red:st==='Próximo'?P.amber:P.green }}>{fmtDate(r.proxima_troca)}</td>
                  <td style={{ padding:'9px 12px', whiteSpace:'nowrap', fontWeight:600,
                    color: (() => { const calc=calcProxima(r.ultima_troca,r.periodicidade); const s=calcStatusCanula(calc); return s==='Atrasado'?P.red:s==='Próximo'?P.amber:P.green; })() }}>
                    {fmtDate(calcProxima(r.ultima_troca, r.periodicidade)) || '—'}
                  </td>
                  <td style={{ padding:'9px 12px' }}><StatusBadge status={st} map={STATUS_CANULA} /></td>
                  <td style={{ padding:'9px 12px', whiteSpace:'nowrap' }}>
                    <div style={{ display:'flex', gap:6 }}>
                      <Btn small color={P.green} onClick={()=>registrarTroca(r)}>✔ Troca</Btn>
                      <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
                      <GhostBtn small onClick={()=>deleteDoc(doc(db,col,r.id))}>🗑</GhostBtn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={edit?'Editar':'Novo Paciente'} onClose={close}>
          {config.fields.map(f => (
            <Field key={f.key} label={f.label} required={f.required}>
              {f.type==='select'
                ? <Sel value={form[f.key]||''} onChange={v=>setForm(x=>({...x,[f.key]:v}))} options={f.options||[]} placeholder="Selecionar…" />
                : f.type==='date'
                ? <input type="date" value={form[f.key]||''} onChange={e=>{
                    const nf={...form,[f.key]:e.target.value};
                    if (f.key==='ultima_troca'&&nf.periodicidade) nf.proxima_troca=calcProxima(e.target.value,nf.periodicidade);
                    setForm(nf);
                  }} style={inp} />
                : <input value={form[f.key]||''} onChange={e=>{
                    const nf={...form,[f.key]:e.target.value};
                    if (f.key==='periodicidade'&&nf.ultima_troca) nf.proxima_troca=calcProxima(nf.ultima_troca,e.target.value);
                    setForm(nf);
                  }} placeholder={f.placeholder||''} style={inp} />
              }
            </Field>
          ))}
          {tipo !== 'bradesco' && (
            <Field label="Próxima troca (auto-calculada)">
              <input type="date" value={form.proxima_troca||''} onChange={e=>setForm(x=>({...x,proxima_troca:e.target.value}))} style={inp} />
            </Field>
          )}
          <Field label="Atualização / Observação">
            <textarea value={form.atualizacao||''} onChange={e=>setForm(x=>({...x,atualizacao:e.target.value}))} rows={2} style={{ ...inp, resize:'vertical' }} />
          </Field>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.paciente?.trim()}>{saving?'Salvando…':'Salvar'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Pipeline Kanban ────────────────────────────────────────────────────────────
function PipelineCard({ r, onChangeStatus, statusOptions = STATUS_AGENDADAS, statusColorMap = STATUS_COLOR }) {
  const [menu, setMenu] = useState(false);
  const sc = statusColorMap[r.status] || { bg:'#f1f5f9', text:'#64748b' };
  return (
    <div style={{ background:'#fff', borderRadius:10, border:'1px solid #e2e8f0', padding:'10px 12px', marginBottom:8,
      boxShadow:'0 1px 3px rgba(0,0,0,0.05)', borderLeft:`3px solid ${sc.text}` }}>
      <div style={{ fontWeight:700, color:'#1e293b', fontSize:'0.85rem', marginBottom:4 }}>
        {r.paciente}
        {r.origem_fluxo==='captacao_previa' && (
          <span style={{ marginLeft:6, background:'#ede9fe', color:'#7c3aed', fontSize:'0.65rem', fontWeight:700, padding:'1px 6px', borderRadius:10 }}>Captação</span>
        )}
      </div>
      {r.operadora && <OpBadge op={r.operadora} />}
      {r.tipo && <span style={{ marginLeft:4, fontSize:'0.7rem', color:'#64748b' }}>{r.tipo}</span>}
      {(r.data||r.horario) && (
        <div style={{ color:'#64748b', fontSize:'0.75rem', marginTop:4 }}>{fmtDate(r.data)}{r.horario?` · ${r.horario}`:''}</div>
      )}
      {(r.local_origem||r.local_destino) && (
        <div style={{ color:'#94a3b8', fontSize:'0.71rem', marginTop:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          📍 {r.local_origem||'—'}{r.local_destino?` → ${r.local_destino}`:''}
        </div>
      )}
      {(r.cep_origem||r.cep_destino) && (
        <div style={{ color:'#94a3b8', fontSize:'0.69rem', marginTop:1 }}>
          CEP: {r.cep_origem||'—'} → {r.cep_destino||'—'}
        </div>
      )}
      <div style={{ marginTop:8, position:'relative' }}>
        <button onClick={()=>setMenu(v=>!v)} style={{ width:'100%', textAlign:'left', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:'0.72rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <StatusBadge status={r.status} small map={statusColorMap} />
          <span style={{ color:'#94a3b8' }}>▾</span>
        </button>
        {menu && (
          <div style={{ position:'absolute', bottom:'calc(100% + 4px)', left:0, right:0, zIndex:200, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', maxHeight:220, overflowY:'auto' }}>
            {statusOptions.filter(s=>s!==r.status).map(s=>(
              <div key={s} onClick={()=>{ onChangeStatus(r,s); setMenu(false); }}
                style={{ padding:'7px 12px', cursor:'pointer', fontSize:'0.8rem' }}
                onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                onMouseLeave={e=>e.currentTarget.style.background=''}>
                <StatusBadge status={s} small map={statusColorMap} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TabPipeline({ data, statusOptions = STATUS_AGENDADAS, statusColorMap = STATUS_COLOR }) {
  const col = getCollectionName('rm_agendadas');
  const [search, setSearch] = useState('');
  const [fOp,    setFOp]    = useState('');

  const filtered = useMemo(() => {
    let r = data;
    if (search) r = r.filter(x => x.paciente?.toLowerCase().includes(search.toLowerCase()));
    if (fOp)    r = r.filter(x => x.operadora === fOp);
    return r;
  }, [data, search, fOp]);

  const byColuna = useMemo(() => {
    const map = {};
    PIPELINE_COLUNAS.forEach(c => { map[c.key] = []; });
    filtered.forEach(r => {
      const found = PIPELINE_COLUNAS.find(c => c.statuses.includes(r.status));
      if (found) map[found.key].push(r);
    });
    return map;
  }, [filtered]);

  const terminal = useMemo(() => filtered.filter(r => PIPELINE_TERMINAL.includes(r.status)), [filtered]);

  const kpis = useMemo(() => ({
    total:     data.length,
    previa:    data.filter(r=>r.status==='Prévia').length,
    agendado:  data.filter(r=>['Autorizado','Pacote'].includes(r.status)).length,
    realizado: data.filter(r=>r.status==='Realizado').length,
    cancelado: data.filter(r=>PIPELINE_TERMINAL.includes(r.status)).length,
  }), [data]);

  const changeStatus = async (r, status) => {
    await updateDoc(doc(db, col, r.id), { status, updatedAt: serverTimestamp() });
  };

  return (
    <div>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:20 }}>
        <KpiCard label="Total"            value={kpis.total}     color={P.muted}   />
        <KpiCard label="Prévias"          value={kpis.previa}    color='#7c3aed'   />
        <KpiCard label="Agendadas"        value={kpis.agendado}  color={P.green}   />
        <KpiCard label="Realizadas"       value={kpis.realizado} color='#0d9488'   />
        <KpiCard label="Canceladas/Neg."  value={kpis.cancelado} color={P.red}     />
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input placeholder="Buscar paciente…" value={search} onChange={e=>setSearch(e.target.value)}
          style={{ border:`1px solid ${P.border}`, borderRadius:8, padding:'7px 12px', fontSize:'0.84rem', flex:1, minWidth:160, outline:'none' }} />
        <select value={fOp} onChange={e=>setFOp(e.target.value)}
          style={{ border:`1px solid ${P.border}`, borderRadius:8, padding:'7px 10px', fontSize:'0.84rem', color:fOp?P.text:P.muted }}>
          <option value="">Todas operadoras</option>
          {OPERADORAS.map(o=><option key={o} value={o}>{o}</option>)}
        </select>
        {(search||fOp) && (
          <button onClick={()=>{ setSearch(''); setFOp(''); }}
            style={{ background:'none', border:`1px solid ${P.border}`, borderRadius:8, padding:'7px 12px', cursor:'pointer', fontSize:'0.82rem', color:P.muted }}>
            Limpar
          </button>
        )}
      </div>

      <div style={{ display:'flex', gap:12, overflowX:'auto', paddingBottom:16 }}>
        {PIPELINE_COLUNAS.map(coluna => (
          <div key={coluna.key} style={{ minWidth:220, flex:'0 0 220px' }}>
            <div style={{ background:coluna.color, color:'#fff', fontWeight:700, fontSize:'0.78rem', padding:'8px 12px', borderRadius:'8px 8px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span>{coluna.label}</span>
              <span style={{ background:'rgba(255,255,255,0.25)', borderRadius:10, padding:'1px 8px', fontWeight:800 }}>{byColuna[coluna.key]?.length||0}</span>
            </div>
            <div style={{ background:'#f8fafc', borderRadius:'0 0 8px 8px', border:'1px solid #e2e8f0', borderTop:'none', padding:'10px 8px', minHeight:100 }}>
              {(byColuna[coluna.key]||[]).length === 0
                ? <div style={{ color:'#cbd5e1', fontSize:'0.75rem', textAlign:'center', padding:'24px 0' }}>Vazio</div>
                : (byColuna[coluna.key]||[]).map(r => <PipelineCard key={r.id} r={r} onChangeStatus={changeStatus} statusOptions={statusOptions} statusColorMap={statusColorMap} />)
              }
            </div>
          </div>
        ))}
      </div>

      {terminal.length > 0 && (
        <div style={{ marginTop:20 }}>
          <div style={{ fontSize:'0.78rem', fontWeight:700, color:P.muted, textTransform:'uppercase', letterSpacing:.5, marginBottom:10 }}>
            Cancelados / Negados / Hospitalizados ({terminal.length})
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {terminal.map(r=>(
              <div key={r.id} style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 14px', fontSize:'0.8rem' }}>
                <strong style={{ color:P.text }}>{r.paciente}</strong>
                <span style={{ marginLeft:8 }}><StatusBadge status={r.status} small map={statusColorMap} /></span>
                {r.data&&<span style={{ marginLeft:8, color:P.muted }}>{fmtDate(r.data)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function GestaoRemocoes() {
  const [tab, setTab] = useState('agendadas');
  const [data, setData] = useState({ agendadas:[], para_agendar:[], contato:[], seriadas:[], canulas:[] });
  const [canceladosMap, setCanceladosMap] = useState({});

  // ── Admin (para gerenciar tipos de Status) ──────────────────────────────────
  const currentUser = auth.currentUser;
  const [userRoles, setUserRoles] = useState([]);
  const isSuperAdmin = currentUser?.email === 'alexandrebareaoliveira@gmail.com';
  const isAdmin = isSuperAdmin || userRoles.includes('admin') || userRoles.includes('administrador');

  useEffect(() => {
    if (!currentUser?.email) return;
    const q = query(collection(db, getCollectionName('users')), where('email', '==', currentUser.email.toLowerCase()));
    return onSnapshot(q, snap => {
      if (!snap.empty) {
        const u = snap.docs[0].data();
        setUserRoles(Array.isArray(u.roles) ? u.roles : (u.role ? [u.role] : []));
      }
    }, () => {});
  }, [currentUser?.email]);

  // ── Empresas de Remoção e Solicitantes (config compartilhada com TabelasHCAdmin.js) ─
  const [empresasRemocao, setEmpresasRemocao] = useState([]);
  useEffect(() => {
    const q = query(collection(db, getCollectionName('cfg_tabelas_hc')), where('tipo', '==', 'empresa_remocao'));
    return onSnapshot(q, snap => {
      setEmpresasRemocao(snap.docs.map(d => d.data().valor).filter(Boolean).sort());
    }, () => {});
  }, []);

  const [solicitantesRemocao, setSolicitantesRemocao] = useState([]);
  useEffect(() => {
    const q = query(collection(db, getCollectionName('cfg_tabelas_hc')), where('tipo', '==', 'solicitante'));
    return onSnapshot(q, snap => {
      setSolicitantesRemocao(snap.docs.map(d => d.data().valor).filter(Boolean).sort());
    }, () => {});
  }, []);

  // ── Base de pacientes (Cadastro de Pacientes + Captação/Implantação), pra
  // autocompletar nome/idade/data de nascimento ao criar uma remoção ─────────
  const [pacientesBase, setPacientesBase] = useState([]);
  useEffect(() => {
    let cadastro = [], captacao = [];
    const sync = () => setPacientesBase([...cadastro, ...captacao]);
    const unsub1 = onSnapshot(collection(db, getCollectionName('escalas_pacientes')), snap => {
      cadastro = snap.docs.map(d => ({ id: d.id, nome: d.data().nome || '', dataNascimento: d.data().dataNascimento || '', _origem: 'cadastro' })).filter(p => p.nome);
      sync();
    }, () => {});
    const unsub2 = onSnapshot(query(collection(db, getCollectionName('captacao_pacientes')), campos('nome', 'dataNascimento')), snap => {
      captacao = snap.docs.map(d => ({ id: d.id, nome: d.data().nome || '', dataNascimento: d.data().dataNascimento || '', _origem: 'captacao' })).filter(p => p.nome);
      sync();
    }, () => {});
    return () => { unsub1(); unsub2(); };
  }, []);

  // ── Status de Remoções (editável pelo admin) ────────────────────────────────
  const [statusList, setStatusList] = useState(null); // null = ainda carregando
  useEffect(() => {
    const colStatus = getCollectionName('rm_status_config');
    const ref = doc(db, colStatus, 'status_agendadas');
    return onSnapshot(ref, async snap => {
      if (snap.exists()) {
        setStatusList(snap.data().statuses || []);
      } else {
        const seed = STATUS_AGENDADAS.map(nome => ({
          nome, corBg: STATUS_COLOR[nome]?.bg, corText: STATUS_COLOR[nome]?.text,
        }));
        setStatusList(seed);
        try { await setDoc(ref, { statuses: seed, editadoEm: serverTimestamp() }); } catch (e) { console.warn('[GestaoRemocoes] seed status_agendadas:', e.message); }
      }
    }, () => {});
  }, []);

  const statusOptions = useMemo(
    () => (statusList || STATUS_AGENDADAS.map(nome => ({ nome }))).map(s => s.nome),
    [statusList]
  );
  const statusColorMap = useMemo(() => {
    const map = {};
    (statusList || []).forEach(s => { map[s.nome] = { bg: s.corBg, text: s.corText }; });
    return Object.keys(map).length ? map : STATUS_COLOR;
  }, [statusList]);

  useEffect(() => {
    const unsubs = [];
    const loadCol = (col, key) => {
      const q = query(collection(db, getCollectionName(col)), orderBy('criadoEm','desc'));
      unsubs.push(onSnapshot(q, snap =>
        setData(prev => ({ ...prev, [key]: snap.docs.map(d => ({ id:d.id, ...d.data() })) }))
      ));
    };
    loadCol('rm_agendadas',    'agendadas');
    loadCol('rm_para_agendar', 'para_agendar');
    loadCol('rm_contato',      'contato');
    loadCol('rm_seriadas',     'seriadas');
    loadCol('rm_canulas',      'canulas');
    return () => unsubs.forEach(u => u());
  }, []);

  // Pacientes cancelados pela operadora na Captação — usado para marcar remoções vinculadas
  useEffect(() => {
    const q = query(
      collection(db, getCollectionName('captacao_pacientes')),
      where('canceladoPelaOperadora', '==', true),
      campos('canceladoEm')
    );
    return onSnapshot(q, snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = { canceladoEm: d.data().canceladoEm || null }; });
      setCanceladosMap(map);
    }, () => {});
  }, []);

  const confirmarAgendamento = async item => {
    const colPA = getCollectionName('rm_para_agendar');
    const colAg = getCollectionName('rm_agendadas');
    await addDoc(collection(db, colAg), {
      paciente:          item.paciente,
      operadora:         item.plano || '',
      motivo:            item.motivo || '',
      tipo:              item.tipo_rm || 'DESOSPITALIZAÇÃO',
      data:              item.data_agendamento || '',
      horario:           item.horario || '',
      solicitante:       'Núcleo Captação',
      status:            'Em análise',
      local_origem:      item.local_origem || item.local || '',
      local_destino:     item.local_destino || '',
      cep_origem:        item.cep_origem || '',
      cep_destino:       item.cep_destino || '',
      tipo_deslocamento: item.tipo_deslocamento || 'so_ida',
      tem_medico:        item.tem_medico || false,
      tem_o2:            item.tem_o2 || false,
      observacoes:       item.observacao || '',
      captacao_id:       item.captacao_id || '',
      num_iw:            item.num_iw || '',
      filial:            item.filial || '',
      origem_fluxo:      item.origem_fluxo || 'manual',
      ...(item.origem === 'IW' ? { origem: 'IW', iw_evolucao: item.iw_evolucao || '', iw_data_pedido: item.iw_data_pedido || '', iw_profissional: item.iw_profissional || '', solicitante: 'Núcleo' } : {}),
      criadoEm:          serverTimestamp(),
    });
    await deleteDoc(doc(db, colPA, item.id));
    // Sync status da task de Remoções na ocorrência correspondente
    if (item.captacao_id) {
      try {
        const ocSnap = await getDocs(query(
          collection(db, getCollectionName('ocorrencias_v2')),
          where('captacaoId', '==', item.captacao_id)
        ));
        if (!ocSnap.empty) {
          const tarefasCol = collection(db, getCollectionName('ocorrencias_v2'), ocSnap.docs[0].id, 'tarefas');
          const tarefasSnap = await getDocs(query(tarefasCol, where('area', '==', 'Remoções')));
          await Promise.all(tarefasSnap.docs.map(d =>
            updateDoc(d.ref, { status: 'em_andamento', atualizadoEm: serverTimestamp() })
          ));
        }
      } catch (e) { console.warn('[GestaoRemocoes] sync tarefa remoção:', e.message); }
    }
    setTab('agendadas');
  };

  const pendentesManual = data.para_agendar.filter(x => x.origem_fluxo !== 'captacao_previa').length;
  const pendentesImplantacao = data.para_agendar.filter(x => x.origem_fluxo === 'captacao_previa').length;

  const tabStyle = k => ({
    padding:'8px 16px', borderRadius:'8px 8px 0 0',
    border:`1px solid ${P.border}`, borderBottom: k===tab ? `2px solid ${P.primary}` : `1px solid ${P.border}`,
    background: k===tab ? P.white : '#f1f5f9',
    color: k===tab ? P.primary : P.muted,
    fontWeight: k===tab ? 800 : 600, fontSize:'0.8rem', cursor:'pointer',
    position:'relative', top:'1px', whiteSpace:'nowrap',
  });

  return (
    <div style={{ background:P.bg, minHeight:'100vh', padding:'24px 24px 60px' }}>
        <style>{`
          .rm-cancelada td { color: #e2e8f0 !important; }
          .rm-cancelada:hover { background: #1f2937 !important; }
        `}</style>
        <div style={{ marginBottom:20 }}>
          <h1 style={{ margin:0, fontSize:'1.4rem', fontWeight:800, color:P.text }}>🚑 Gestão de Remoções</h1>
          <p style={{ margin:'4px 0 0', color:P.muted, fontSize:'0.83rem' }}>Agendamento, cânulas e sondas dos pacientes homecare</p>
        </div>

        <div style={{ display:'flex', flexWrap:'wrap', gap:4, borderBottom:`1px solid ${P.border}`, marginBottom:0 }}>
          {TABS.map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} style={tabStyle(t.k)}>
              {t.l}
              {t.k==='para_agendar' && pendentesManual>0 && (
                <span style={{ background:P.red, color:'#fff', borderRadius:20, padding:'1px 6px', fontSize:'0.65rem', fontWeight:800, marginLeft:6 }}>{pendentesManual}</span>
              )}
              {t.k==='implantacao_agendar' && pendentesImplantacao>0 && (
                <span style={{ background:P.red, color:'#fff', borderRadius:20, padding:'1px 6px', fontSize:'0.65rem', fontWeight:800, marginLeft:6 }}>{pendentesImplantacao}</span>
              )}
            </button>
          ))}
        </div>

        <div style={{ background:P.white, border:`1px solid ${P.border}`, borderTop:'none', borderRadius:'0 0 12px 12px', padding:'22px 20px' }}>
          {tab==='pipeline'     && <TabPipeline     data={data.agendadas} statusOptions={statusOptions} statusColorMap={statusColorMap} />}
          {tab==='agendadas'    && <TabAgendadas    data={data.agendadas} canceladosMap={canceladosMap} statusOptions={statusOptions} statusColorMap={statusColorMap} isAdmin={isAdmin} empresasRemocao={empresasRemocao} solicitantesRemocao={solicitantesRemocao} pacientesBase={pacientesBase} />}
          {tab==='dashboard_valores' && <TabDashboardValores data={data.agendadas} />}
          {tab==='conciliacao'  && <ConciliacaoRemocoes />}
          {tab==='aph'          && <AphSOS registros={[...data.agendadas, ...data.para_agendar]} />}
          {tab==='para_agendar' && <TabParaAgendar  data={data.para_agendar} onConfirmar={confirmarAgendamento} canceladosMap={canceladosMap} />}
          {tab==='implantacao_agendar' && <TabParaAgendar data={data.para_agendar} onConfirmar={confirmarAgendamento} canceladosMap={canceladosMap} onlyImplantacao />}
          {tab==='seriadas'     && <TabSeriadas      data={data.seriadas} />}
          {tab==='cancelados'   && <TabCancelados agendadas={data.agendadas} paraAgendar={data.para_agendar} canceladosMap={canceladosMap} />}
        </div>
    </div>
  );
}
